import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Grid,
  CircularProgress,
  LinearProgress,
  Divider,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Assignment as ProjectIcon,
  Timeline as ProgressIcon,
  AttachMoney as MoneyIcon,
  Launch as LaunchIcon,
  ArrowForward as ArrowIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  Help as HelpIcon,
  Assessment as StatsIcon,
  AttachFile as FileIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  linkChatFileToProject,
  searchChatFileProjects,
  requestMachiningSimilarByFile,
  requestToolSheetReviewByFile,
  requestShapeAnalysisByFile,
  fetchMachiningThumbnail,
} from '../../lib/api/chatApi';
import * as fileApi from '../../lib/api/file';

// 견적서 컴포넌트들 import
import { 
  EstimateRegistrationConfirm, 
  EstimateRegistrationForm, 
  EstimateRegistrationComplete 
} from './estimate/EstimateComponents';

// 제조 작업 완료 컴포넌트 import
import ManufacturingTaskComplete from './ManufacturingTaskComplete';
import RagSourceLink from './RagSourceLink';
import ChatImage from './ChatImage';
import { isImageFile } from '../../utils/fileKind';

// 목록형 응답 공용 컴팩트 테이블 + 헬퍼 (다른 파일과 공유하기 위해 별도 모듈로 분리됨)
import {
  openChatUrl,
  BotListFooter,
  renderTitleCell,
  renderStageCell,
  renderAmountCell,
  renderDateCell,
  CompactListTable,
} from './BotListTable';


// 파일 연결 선택 버튼. 휴대폰에서는 줄 간격·위아래 여백이 커서 버튼 하나가 3~4줄 높이가 됐다
// (2026-10-15 사용자 "버튼이 너무 크다"). 휴대폰에서만 여백을 줄이고 설명 글은 촘촘하게 두 줄까지만 보인다.
const CHOICE_BUTTON_SX = {
  textTransform: 'none',
  justifyContent: 'flex-start',
  py: { xs: 0.75, sm: 1.5 },
  px: { xs: 1.25, sm: 2 },
  borderRadius: 2,
  lineHeight: 1.3,
  '& .MuiButton-startIcon': { mr: { xs: 1, sm: 1.25 } },
};
const CHOICE_TEXT_SX = {
  textAlign: 'left',
  minWidth: 0,
  width: '100%',
  whiteSpace: 'normal',
  wordBreak: 'keep-all',
  '& .MuiTypography-root': { display: 'block', lineHeight: 1.35 },
  '& .MuiTypography-caption': {
    display: '-webkit-box',
    WebkitLineClamp: { xs: 2, sm: 'unset' },
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
};

const clampText = (lines = 2) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/**
 * RAG 검색 결과 카드의 도면/형상 썸네일. 인증 헤더가 필요해 <img src="..."> 에 API URL을
 * 바로 넣을 수 없으므로(로그인 세션 쿠키/토큰 필요), axios(client)로 blob을 받아
 * objectURL로 변환해 렌더링한다. 참고 패턴: src/hooks/useProfileImage.js
 */
const RagThumbnail = ({ path, size = 88, fill = false, onClick }) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const loadThumbnail = async () => {
      if (!path) {
        setImageUrl(null);
        return;
      }
      try {
        const response = await fetchMachiningThumbnail(path);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setImageUrl(objectUrl);
      } catch (error) {
        if (!cancelled) {
          setImageUrl(null);
        }
      }
    };

    loadThumbnail();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [path]);

  if (!imageUrl) {
    // 로딩 중이거나 실패한 경우 빈 placeholder만 표시한다.
    return (
      <Box
        sx={{
          width: fill ? '100%' : size,
          height: fill ? 200 : size,
          flexShrink: 0,
          borderRadius: 1,
          backgroundColor: '#f0f0f0',
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={imageUrl}
      alt="도면/형상 썸네일"
      onClick={onClick}
      sx={fill ? {
        // 펼친 상세에서 쓰는 큰 그림: 폭에 맞추고 비율 유지, 눌러서 전체 화면 확대.
        width: '100%',
        maxHeight: 320,
        objectFit: 'contain',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        cursor: onClick ? 'zoom-in' : 'default',
      } : {
        width: size,
        height: size,
        flexShrink: 0,
        objectFit: 'cover',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
      }}
    />
  );
};

// 프로젝트 목록 카드 컴포넌트
const ProjectListCard = ({ project, onSelect }) => {
  const getGradeColor = (grade) => {
    const colors = {
      '견적': 'warning',
      '요구사항분석': 'info', 
      '수주': 'success',
      '개발': 'primary',
      '테스트': 'secondary',
      '완료': 'success'
    };
    return colors[grade] || 'default';
  };

  const getProjectTypeIcon = (prjNo) => {
    if (prjNo?.includes('BI_PJ')) return '🔧';
    if (prjNo?.includes('BI_MA')) return '🔧';
    if (prjNo?.includes('PRJ')) return '📋';
    return '📁';
  };

  return (
    <Card 
      sx={{ 
        mb: 0.75,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          backgroundColor: '#f8f9fa'
        },
        border: '1px solid #dfe3e8',
        borderRadius: 1
      }}
      onClick={() => onSelect(project)}
    >
      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack spacing={0.75}>
          {/* 헤더: 프로젝트 코드와 단계 */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography 
              variant="caption"
              sx={{ 
                fontFamily: 'monospace', 
                fontWeight: 'bold',
                color: 'primary.main',
                backgroundColor: 'primary.50',
                px: 0.75,
                py: 0.35,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                maxWidth: 'calc(100% - 92px)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {getProjectTypeIcon(project.prjNo)} {project.prjNo}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                label={project.gradeDescription || '단계미정'}
                size="small"
                color={getGradeColor(project.gradeDescription)}
                variant="outlined"
                sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }}
              />
              <Tooltip title="상세보기">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="상세보기"
                  sx={{ width: 26, height: 26 }}
                >
                  <ArrowIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* 프로젝트명 */}
          <Typography 
            variant="subtitle2"
            sx={{ 
              fontWeight: 'medium',
              lineHeight: 1.25,
              color: 'text.primary',
              ...clampText(2)
            }}
          >
            {project?.cstNm || project.projectName || '프로젝트명 없음'}
          </Typography>

          {/* 상세 정보 */}
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0, maxWidth: '100%' }}>
              <BusinessIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                매출처 {project.partnerInc || '미정'}
              </Typography>
            </Stack>
            
            {project.pmUsr && (
              <Stack direction="row" alignItems="center" spacing={0.4}>
                <PersonIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  PM {project.pmUsr}
                </Typography>
              </Stack>
            )}

            {project.expSales && (
              <Stack direction="row" alignItems="center" spacing={0.4}>
                <MoneyIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  예상매출 {project.expSales?.toLocaleString()}만원
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const getFileLinkErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const FileUploadConnectAction = ({
  message,
  onProjectSelect,
  onBotMessage,
  alreadyLinked = false,
  connectedData,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const data = message?.data || {};
  const fileUuid = data.fileUuid || message?.fileUuid;
  const fileName = data.fileName || message?.fileNm || '파일';
  const [view, setView] = useState('choice');
  const [searchWords, setSearchWords] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [linkedProject, setLinkedProject] = useState(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [reviewingToolSheet, setReviewingToolSheet] = useState(false);
  const [analyzingShape, setAnalyzingShape] = useState(false);

  const loadProjects = useCallback(async (keyword = '') => {
    setSearching(true);
    setSearchError('');
    setProjects([]);
    setSelectedProject(null);

    try {
      const response = await searchChatFileProjects(keyword.trim());
      const list = Array.isArray(response?.data?.list) ? response.data.list : [];
      const uniqueProjects = Array.from(
        new Map(list.filter(project => project?.prjUuid).map(project => [project.prjUuid, project])).values(),
      );

      setProjects(uniqueProjects.slice(0, 20));
      setSelectedProject(current =>
        current && uniqueProjects.some(project => project.prjUuid === current.prjUuid)
          ? current
          : null,
      );
    } catch (error) {
      const errorMessage = getFileLinkErrorMessage(error, '프로젝트 목록을 불러오지 못했습니다.');
      setSearchError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSearching(false);
    }
  }, [enqueueSnackbar]);

  const handleOpenProjectSearch = useCallback(() => {
    setView('project');
    if (projects.length === 0 && !searching) {
      loadProjects(searchWords);
    }
  }, [loadProjects, projects.length, searching, searchWords]);

  const handleSearch = useCallback((event) => {
    event.preventDefault();
    loadProjects(searchWords);
  }, [loadProjects, searchWords]);

  const handleLinkProject = useCallback(async () => {
    if (!fileUuid) {
      enqueueSnackbar('파일 식별 정보가 없습니다. 파일을 다시 업로드해주세요.', { variant: 'error' });
      return;
    }
    if (!selectedProject?.prjUuid) {
      enqueueSnackbar('연결할 프로젝트를 선택해주세요.', { variant: 'warning' });
      return;
    }

    setLinking(true);
    try {
      const response = await linkChatFileToProject(fileUuid, selectedProject.prjUuid);
      const responseData = response?.data || {};
      const responseProject = responseData.botResponse?.data || responseData.info?.project;
      setLinkedProject({
        ...selectedProject,
        ...(responseProject && typeof responseProject === 'object' ? responseProject : {}),
      });
      setView('complete');

      if (responseData.botMessage) {
        onBotMessage?.(responseData.botMessage);
      }
      enqueueSnackbar('파일을 프로젝트에 연결했습니다.', { variant: 'success' });
    } catch (error) {
      const errorMessage = getFileLinkErrorMessage(error, '파일을 프로젝트에 연결하지 못했습니다.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLinking(false);
    }
  }, [enqueueSnackbar, fileUuid, onBotMessage, selectedProject]);

  const handleFindSimilarMachining = useCallback(async () => {
    if (!fileUuid) {
      enqueueSnackbar('파일 식별 정보가 없습니다. 파일을 다시 업로드해주세요.', { variant: 'error' });
      return;
    }

    setAnalyzing(true);
    try {
      await requestMachiningSimilarByFile(fileUuid);
      enqueueSnackbar('도면 형상을 분석하고 있습니다. 결과는 곧 채팅에 표시됩니다.', { variant: 'info' });
    } catch (error) {
      const errorMessage = getFileLinkErrorMessage(error, '유사 공정 검색 요청에 실패했습니다.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setAnalyzing(false);
    }
  }, [enqueueSnackbar, fileUuid]);

  const handleReviewToolSheet = useCallback(async () => {
    if (!fileUuid) {
      enqueueSnackbar('파일 식별 정보가 없습니다. 파일을 다시 업로드해주세요.', { variant: 'error' });
      return;
    }

    setReviewingToolSheet(true);
    try {
      await requestToolSheetReviewByFile(fileUuid);
      enqueueSnackbar('툴시트를 기준자료와 대조하고 있습니다. 결과는 곧 채팅에 표시됩니다.', { variant: 'info' });
    } catch (error) {
      const errorMessage = getFileLinkErrorMessage(error, '가공조건 검토 요청에 실패했습니다.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setReviewingToolSheet(false);
    }
  }, [enqueueSnackbar, fileUuid]);

  const handleAnalyzeShape = useCallback(async () => {
    if (!fileUuid) {
      enqueueSnackbar('파일 식별 정보가 없습니다. 파일을 다시 업로드해주세요.', { variant: 'error' });
      return;
    }

    setAnalyzingShape(true);
    try {
      await requestShapeAnalysisByFile(fileUuid);
      enqueueSnackbar('STP 형상을 분석하고 있습니다. 결과는 곧 채팅에 표시됩니다.', { variant: 'info' });
    } catch (error) {
      const errorMessage = getFileLinkErrorMessage(error, '형상 분석 요청에 실패했습니다.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setAnalyzingShape(false);
    }
  }, [enqueueSnackbar, fileUuid]);

  if (alreadyLinked || view === 'complete') {
    const completedProject = linkedProject || connectedData?.project || connectedData?.projectData || connectedData;
    return (
      <Alert severity="success" sx={{ maxWidth: '100%' }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            프로젝트 연결 완료
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            <strong>{fileName}</strong> 파일을 아래 영업 프로젝트에 연결했습니다.
          </Typography>
          <Typography variant="body2">
            {completedProject?.prjNo || completedProject?.projectNo || '프로젝트 번호 없음'} ·{' '}
            {completedProject?.cstNm || completedProject?.projectName || '프로젝트명 없음'}
          </Typography>
        </Stack>
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FileIcon color="primary" />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                파일 업로드 완료
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">파일명</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {fileName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">파일 타입</Typography>
                <Typography variant="body2">{data.fileType}</Typography>
              </Grid>
              {/* 이미지면 파일 이름만이 아니라 무엇을 올렸는지 바로 보이게 미리보기(누르면 크게) */}
              {fileUuid && (isImageFile({ fileNm: fileName }) || data.fileType === '이미지') && (
                <Grid item xs={12}>
                  <ChatImage fileUuid={fileUuid} fileUrl={data.fileUrl || message?.fileUrl} fileName={fileName} />
                </Grid>
              )}
              {(data.projectNo || data.suggestedProjectNo) && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      추출된 프로젝트 번호: <strong>{data.projectNo || data.suggestedProjectNo}</strong> (프로젝트 미발견)
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* 서버가 파일 종류를 미리 알아본 경우, 알맞은 작업을 먼저 제안한다.
          자동 실행이 아니라 제안이다 — 인식이 틀렸을 때 사용자가 바로 알아챌 수 있어야 한다. */}
      {view === 'choice' && data.detectedKind === 'TOOLSHEET' && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button
              size="small"
              variant="contained"
              disabled={!fileUuid || reviewingToolSheet}
              startIcon={reviewingToolSheet ? <CircularProgress size={14} /> : null}
              onClick={handleReviewToolSheet}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              검증하기
            </Button>
          )}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            가공 툴시트로 보입니다{data.detectedRowCount > 0 ? ` (공구 조건 ${data.detectedRowCount}행 인식)` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            기준자료와 대조해 행별로 적합/주의/문제를 판정할까요?
          </Typography>
        </Alert>
      )}
      {view === 'choice' && data.detectedKind === 'STP_MODEL' && data.stpShapeAnalysisAvailable === true && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button
              size="small"
              variant="contained"
              disabled={!fileUuid || analyzingShape}
              startIcon={analyzingShape ? <CircularProgress size={14} /> : null}
              onClick={handleAnalyzeShape}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              분석하기
            </Button>
          )}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            STP/STEP 3D 설계파일로 보입니다
          </Typography>
          <Typography variant="caption" color="text.secondary">
            치수·부피와 6면도를 분석해 보여드릴까요?
          </Typography>
        </Alert>
      )}
      {view === 'choice' && data.detectedKind === 'DRAWING_IMAGE' && data.imageSimilarAvailable === true && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button
              size="small"
              variant="contained"
              disabled={!fileUuid || analyzing}
              startIcon={analyzing ? <CircularProgress size={14} /> : null}
              onClick={handleFindSimilarMachining}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              찾아보기
            </Button>
          )}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            도면 이미지로 보입니다
          </Typography>
          <Typography variant="caption" color="text.secondary">
            형상이 비슷한 과거 전극·공정 내역을 찾아볼까요?
          </Typography>
        </Alert>
      )}

      {view === 'choice' ? (
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
              이 파일을 어디에 연결할까요?
            </Typography>
            {!fileUuid && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                파일 식별 정보가 없어 연결할 수 없습니다. 파일을 다시 업로드해주세요.
              </Alert>
            )}
            <Stack spacing={{ xs: 1, sm: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<BusinessIcon />}
                disabled={!fileUuid}
                onClick={handleOpenProjectSearch}
                sx={CHOICE_BUTTON_SX}
              >
                <Box sx={CHOICE_TEXT_SX}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>영업 프로젝트</Typography>
                  <Typography variant="caption" color="text.secondary">프로젝트 검색 후 문서로 연결</Typography>
                </Box>
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={<ProjectIcon />}
                onClick={() => onProjectSelect && onProjectSelect('제조 작업 선택')}
                sx={CHOICE_BUTTON_SX}
              >
                <Box sx={CHOICE_TEXT_SX}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>제조 생산계획</Typography>
                  <Typography variant="caption" color="text.secondary">생산계획 검색 후 설계/QC/자재 문서로 연결</Typography>
                </Box>
              </Button>
              {data.imageSimilarAvailable === true && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="info"
                  startIcon={analyzing ? <CircularProgress size={16} /> : <SearchIcon />}
                  disabled={!fileUuid || analyzing}
                  onClick={handleFindSimilarMachining}
                  sx={CHOICE_BUTTON_SX}
                >
                  <Box sx={CHOICE_TEXT_SX}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>유사 공정 찾기</Typography>
                    <Typography variant="caption" color="text.secondary">업로드한 도면 형상과 비슷한 과거 전극·공정 내역 조회</Typography>
                  </Box>
                </Button>
              )}
              {data.toolSheetReviewAvailable === true && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="info"
                  startIcon={reviewingToolSheet ? <CircularProgress size={16} /> : <StatsIcon />}
                  disabled={!fileUuid || reviewingToolSheet}
                  onClick={handleReviewToolSheet}
                  sx={CHOICE_BUTTON_SX}
                >
                  <Box sx={CHOICE_TEXT_SX}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>가공조건 검토</Typography>
                    <Typography variant="caption" color="text.secondary">업로드한 툴시트를 기준자료와 대조해 행별로 판정</Typography>
                  </Box>
                </Button>
              )}
              {data.stpShapeAnalysisAvailable === true && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="info"
                  startIcon={analyzingShape ? <CircularProgress size={16} /> : <SearchIcon />}
                  disabled={!fileUuid || analyzingShape}
                  onClick={handleAnalyzeShape}
                  sx={CHOICE_BUTTON_SX}
                >
                  <Box sx={CHOICE_TEXT_SX}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>형상 분석</Typography>
                    <Typography variant="caption" color="text.secondary">STP 파일의 치수·부피와 6면도 분석</Typography>
                  </Box>
                </Button>
              )}
              <Button
                fullWidth
                variant="text"
                color="inherit"
                onClick={() => setView('skipped')}
                sx={{ textTransform: 'none', justifyContent: 'flex-start', py: 1, borderRadius: 2, color: 'text.secondary' }}
              >
                <Typography variant="body2">연결 없이 저장</Typography>
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : view === 'skipped' ? (
        <Alert severity="info">
          파일은 채팅에 저장되었으며 프로젝트에는 연결하지 않았습니다.
        </Alert>
      ) : (
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  연결할 영업 프로젝트 선택
                </Typography>
                <Button size="small" onClick={() => setView('choice')} sx={{ textTransform: 'none' }}>
                  뒤로
                </Button>
              </Stack>
              <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={searchWords}
                  onChange={event => setSearchWords(event.target.value)}
                  placeholder="프로젝트 번호, 사업명, 매출처 검색"
                  inputProps={{ 'aria-label': '연결할 프로젝트 검색' }}
                />
                <Button type="submit" variant="contained" disabled={searching} sx={{ minWidth: 76 }}>
                  {searching ? <CircularProgress size={20} color="inherit" /> : '검색'}
                </Button>
              </Box>

              {searchError && <Alert severity="error">{searchError}</Alert>}
              {!searching && !searchError && projects.length === 0 && (
                <Alert severity="info">검색된 프로젝트가 없습니다.</Alert>
              )}
              <Stack spacing={0.75} sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {projects.map(project => {
                  const selected = selectedProject?.prjUuid === project.prjUuid;
                  return (
                    <Card
                      key={project.prjUuid}
                      component="button"
                      type="button"
                      onClick={() => setSelectedProject(project)}
                      variant="outlined"
                      sx={{
                        width: '100%',
                        p: 1.25,
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.50' : 'background.paper',
                        font: 'inherit',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
                            {project.prjNo || '프로젝트 번호 없음'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', wordBreak: 'break-word' }}>
                            {project.cstNm || '프로젝트명 없음'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[project.cstStatus, project.cstGrade].filter(Boolean).join(' · ') || '상태 미정'}
                          </Typography>
                        </Box>
                        {selected && <CheckIcon color="primary" />}
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>

              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={linking ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                disabled={!selectedProject || linking}
                onClick={handleLinkProject}
                sx={{ textTransform: 'none' }}
              >
                {linking ? '연결 중...' : selectedProject ? `${selectedProject.prjNo || '선택한 프로젝트'}에 연결` : '프로젝트를 선택해주세요'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const ProjectDetailView = ({ project, onNavigate, onCommand }) => {
  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'primary';
    if (percentage >= 20) return 'warning';
    return 'error';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '미정';
    return `${amount.toLocaleString()}만원`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '미정';
    try {
      return new Date(dateStr).toLocaleDateString('ko-KR');
    } catch {
      return dateStr;
    }
  };

  const getProjectTypeText = (prjTyp) => {
    const types = {
      'N': 'SI (System Integration)',
      'M': 'SM (System Maintenance)', 
      'DEV': '개발 프로젝트',
      'CONSULTING': '컨설팅'
    };
    return types[prjTyp] || prjTyp || '미정';
  };

  const getImportanceText = (prjImpt) => {
    const importance = {
      'HIGH': 'High (90%이상)',
      'MEDIUM': 'Medium (50-89%)',
      'LOW': 'Low (50%미만)'
    };
    return importance[prjImpt] || prjImpt || '미정';
  };

  const getImportanceColor = (prjImpt) => {
    const colors = {
      'HIGH': 'error',
      'MEDIUM': 'warning', 
      'LOW': 'success'
    };
    return colors[prjImpt] || 'default';
  };

  const progressValue = Number.parseInt(project?.progressPercentage, 10) || 0;

  // 타이디 카드 - 제목(프로젝트명) + 단계 배지(오른쪽), 예상 매출(핵심 숫자)을 크게,
  // 나머지(프로젝트번호/타입/중요도/고객/담당자/연락처 등)는 2줄의 작은 보조 텍스트로 내렸다.
  // 기존에 6개 카드로 나뉘어 있던 정보를 지우지 않고 그대로 담았다.
  const metaLine1 = [
    project?.prjNo,
    getProjectTypeText(project?.prjTyp),
    project?.partnerInc ? `매출처 ${project.partnerInc}` : null,
    project?.pmUsr ? `PM ${project.pmUsr}` : null,
    project?.expDay ? `완료예정 ${formatDate(project.expDay)}` : null,
  ].filter(Boolean).join(' · ');

  const metaLine2 = [
    (project?.customerNm || project?.partnerInc) ? `고객사 ${project.customerNm || project.partnerInc}` : null,
    project?.chargeNm ? `고객담당 ${project.chargeNm}` : null,
    project?.chargeTel || null,
    project?.chargeEml || null,
    project?.crtNm ? `영업담당 ${project.crtNm}` : null,
    project?.ordrEstDate ? `계약예정 ${formatDate(project.ordrEstDate)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Stack spacing={1.25}>
            {/* 헤더: 제목 + 단계 배지 */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {project?.cstNm || '프로젝트명 없음'}
              </Typography>
              <Chip
                label={project?.currentStage || '단계미정'}
                color="primary"
                variant="filled"
              />
            </Stack>

            {/* 핵심 숫자: 예상 매출 */}
            <Box>
              <Typography variant="caption" color="text.secondary">예상 매출</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatCurrency(project?.expSales)}
              </Typography>
            </Box>

            <Divider />

            {/* 보조 메타(작게) */}
            <Typography variant="caption" color="text.secondary">{metaLine1}</Typography>
            {metaLine2 && <Typography variant="caption" color="text.secondary">{metaLine2}</Typography>}

            {/* 중요도/예산/제품/조달 - 카테고리·특성 칩(중립), 예산확보 여부만 준비상태라 success/default 사용 */}
            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
              <Chip label={getImportanceText(project?.prjImpt)} size="small" color={getImportanceColor(project?.prjImpt)} variant="outlined" />
              <Chip label={project?.bugetYn === 'Y' ? '예산 확보' : '예산 미확보'} size="small" color={project?.bugetYn === 'Y' ? 'success' : 'default'} variant="outlined" />
              <Chip label={project?.productYn === 'Y' ? '제품 포함' : '서비스만'} size="small" color="default" variant="outlined" />
              <Chip label={project?.procurement ? '조달 사업' : '일반 사업'} size="small" color="default" variant="outlined" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* 진행률 정보 */}
      {project?.progressPercentage !== undefined && (
        <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                <ProgressIcon sx={{ fontSize: 16, mr: 0.5 }} />
                진행 현황
              </Typography>

              <Box>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">프로젝트 진행률</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {project?.progressPercentage}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  color={getProgressColor(progressValue)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 참고사항 */}
      {project?.forcastTxt && (
        <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                📝 참고사항
              </Typography>
              <Typography variant="body2" sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 2, 
                borderRadius: 1,
                borderLeft: '3px solid #2196f3',
                lineHeight: 1.6
              }}>
                {project.forcastTxt}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 다음 액션 */}
      {project?.nextActions && project?.nextActions.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              📋 다음 액션
            </Typography>
            <Stack spacing={1}>
              {project.nextActions.map((action, index) => (
                <Box key={index} sx={{ 
                  p: 1, 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 1,
                  borderLeft: '3px solid #2196f3'
                }}>
                  <Typography variant="body2">
                    • {action}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 액션 버튼들 */}
      <Card sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LaunchIcon />}
              onClick={() => onNavigate?.(project?.projectDetailUrl)}
              sx={{ textTransform: 'none' }}
            >
              상세 보기
            </Button>
            {project?.estimateStartCommand && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DescriptionIcon />}
                onClick={() => onCommand?.(project.estimateStartCommand)}
                sx={{ textTransform: 'none' }}
              >
                견적 등록
              </Button>
            )}
            {project?.estimateManageUrl && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<DescriptionIcon />}
                onClick={() => onNavigate?.(project.estimateManageUrl)}
                sx={{ textTransform: 'none' }}
              >
                견적 화면
              </Button>
            )}
            {project?.taxbillManageUrl && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LaunchIcon />}
                onClick={() => onNavigate?.(project.taxbillManageUrl)}
                sx={{ textTransform: 'none' }}
              >
                세금계산서
              </Button>
            )}
            {project?.collectionManageUrl && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<LaunchIcon />}
                onClick={() => onNavigate?.(project.collectionManageUrl)}
                sx={{ textTransform: 'none' }}
              >
                채권/수금
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const SalesTaskSummaryView = ({ data, listSummary, onNavigate, onCommand, onNavigateRoute, onFollowUp }) => {
  const items = data?.items || [];

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {listSummary ? (
        // listSummary가 있으면 요약 한 줄이 건수까지 담고 있으므로, 기존의 긴 안내문 + 건수 칩은 생략한다.
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          {listSummary.summary}
        </Typography>
      ) : (
        <>
          <Alert severity={items.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {data?.summaryMessage}
            </Typography>
          </Alert>

          <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`총 ${data?.totalCount || 0}건`} color="primary" variant="outlined" />
                <Chip label={`지연 ${data?.overdueCount || 0}건`} color="error" variant="outlined" />
                <Chip label={`급한 업무 ${data?.urgentCount || 0}건`} color="warning" variant="outlined" />
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {items.length === 0 ? (
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              확인할 업무가 없습니다.
            </Typography>
          </CardContent>
        </Card>
      ) : listSummary ? (
        // 컴팩트 테이블(B안) - 할 일은 고객명 필드가 따로 없어 title(업무 제목)을 1행, subtitle을
        // 2행(그레이)에 쓴다. 금액 개념이 없는 업무라 금액 칸은 항상 '-'.
        <CompactListTable
          items={items}
          getRowKey={(item, index) => `${item.taskType}-${item.prjUuid || item.invUuid || index}`}
          getActionUrl={(item) => item.actionUrl}
          columns={[
            // 할 일에는 금액 개념이 없어 금액 칸을 빼고 업무명에 그 폭을 준다 - 항상 '-'만 찍히는 칸은 눈만 어지럽힌다.
            { key: 'title', header: '업무', width: '58%', render: (item) => renderTitleCell(item.title, item.subtitle, '업무명 없음') },
            { key: 'stage', header: '단계/상태', width: '24%', render: (item) => renderStageCell(item.urgencyLabel || '예정', item.overdue ? 'error' : 'default') },
            { key: 'date', header: '기한', width: '18%', align: 'right', render: (item) => renderDateCell(item.dueDate) },
          ]}
        />
      ) : (
        <Stack spacing={1.5}>
          {items.map((item, index) => (
            <Card key={`${item.taskType}-${item.prjUuid || item.invUuid || index}`} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {item.title}
                      </Typography>
                      {item.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {item.subtitle}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={item.urgencyLabel || '예정'}
                      size="small"
                      color={item.overdue ? 'error' : item.daysRemaining <= 3 ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </Stack>

                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {item.reason}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {item.dueDate && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`기한 ${item.dueDate}`}
                      />
                    )}
                    {Number.isFinite(item.daysRemaining) && item.daysRemaining !== 2147483647 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={item.daysRemaining < 0 ? `${Math.abs(item.daysRemaining)}일 지남` : `${item.daysRemaining}일 남음`}
                      />
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(item.actionCommand || item.actionUrl) && (
                      <Button
                        size="small"
                        variant="contained"
                        color={item.overdue ? 'error' : 'primary'}
                        onClick={() => {
                          if (item.actionCommand) {
                            onCommand?.(item.actionCommand);
                            return;
                          }
                          onNavigate?.(item.actionUrl);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        {item.actionLabel || '열기'}
                      </Button>
                    )}
                    {item.actionCommand && item.actionUrl && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onNavigate?.(item.actionUrl)}
                        sx={{ textTransform: 'none' }}
                      >
                        화면 열기
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {listSummary && (
        <BotListFooter
          listSummary={listSummary}
          shownCount={items.length}
          onNavigateRoute={onNavigateRoute}
          onFollowUp={onFollowUp}
        />
      )}
    </Box>
  );
};

// 영업 일정(수주 결과별) 목록 뷰 - responseType: SALES_SCHEDULE_LIST
const SalesScheduleListView = ({ data, message, listSummary, onSelect, onNavigate, onNavigateRoute, onFollowUp }) => {
  const items = Array.isArray(data) ? data : [];

  const statusColor = (label) => {
    if (!label) return 'default';
    if (label.startsWith('수주완료')) return 'success';
    if (label.startsWith('진행중')) return 'info';
    if (label.startsWith('실패') || label.includes('실주')) return 'error';
    return 'default';
  };

  // 날짜 컬럼 헤더: 상위 항목이 전부 수주완료면 "계약일"(실제 계약된 날짜), 그 외(진행중/실패/혼합)는
  // 아직 확정 전이라 "기준일"로 둔다. 한 테이블에 한 헤더만 가능해 다수 버킷이 섞인 경우의 절충이다.
  const allWon = items.length > 0 && items.every(item => (item.cstStatus || '').startsWith('수주완료'));
  const dateHeaderLabel = allWon ? '계약일' : '기준일';

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(640px, 100%)' }, maxWidth: '100%' }}>
      {listSummary ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {listSummary.summary}
        </Typography>
      ) : message && (
        <Alert severity="info" sx={{ mb: 1, py: 0.75 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.82rem', lineHeight: 1.45 }}>
            {message}
          </Typography>
        </Alert>
      )}

      {items.length === 0 ? (
        <Card sx={{ border: '1px solid #dfe3e8', borderRadius: 1 }}>
          <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Typography variant="body2" color="text.secondary">
              표시할 영업 건이 없습니다.
            </Typography>
          </CardContent>
        </Card>
      ) : listSummary ? (
        // 컴팩트 테이블(B안) - 단계 칩은 중립색(outlined/default)만 쓴다. success/warning/error는
        // 이 앱에서 좋음/주의/나쁨 전용 색이라 영업 단계 표시에는 쓰지 않는다.
        <CompactListTable
          items={items}
          getRowKey={(item, index) => item.prjUuid || index}
          getActionUrl={(item) => item.actionUrl}
          columns={[
            { key: 'biz', header: '고객 · 사업명', width: '42%', render: (item) => renderTitleCell(item.customerNm, item.cstNm, '거래처 미정') },
            { key: 'stage', header: '단계/상태', width: '24%', render: (item) => renderStageCell(item.stageLabel || item.gradeDescription || item.cstStatus, 'default') },
            { key: 'amount', header: '금액', width: '17%', align: 'right', render: (item) => renderAmountCell(item.expSales) },
            { key: 'date', header: dateHeaderLabel, width: '17%', align: 'right', render: (item) => renderDateCell(item.dueDate) },
          ]}
        />
      ) : (
        <Stack spacing={0.75}>
          {items.map((item, index) => (
            <Card
              key={item.prjUuid || index}
              sx={{
                cursor: 'pointer',
                border: '1px solid #dfe3e8',
                borderRadius: 1,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: 3, backgroundColor: '#f8f9fa' },
              }}
              onClick={() => onSelect && onSelect(item)}
            >
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                        color: 'primary.main',
                        backgroundColor: 'primary.50',
                        px: 0.75,
                        py: 0.35,
                        borderRadius: 1,
                        minWidth: 0,
                        maxWidth: 'calc(100% - 86px)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {item.prjNo}
                    </Typography>
                    <Chip
                      label={item.cstStatus || '상태미정'}
                      size="small"
                      color={statusColor(item.cstStatus)}
                      variant="filled"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }}
                    />
                  </Stack>

                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium', lineHeight: 1.25, ...clampText(2) }}>
                    {item.cstNm || '프로젝트명 없음'}
                  </Typography>

                  {item.reason && (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.35, ...clampText(2) }}>
                      {item.reason}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    {item.gradeDescription && (
                      <Chip label={item.gradeDescription} size="small" variant="outlined" sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }} />
                    )}
                    {item.pmUsr && (
                      <Chip label={`PM ${item.pmUsr}`} size="small" variant="outlined" sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }} />
                    )}
                    {item.dueDate && (
                      <Chip label={`기준일 ${item.dueDate}`} size="small" variant="outlined" sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }} />
                    )}
                  </Stack>

                  {item.actionUrl && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<LaunchIcon fontSize="small" />}
                        onClick={(event) => {
                          event.stopPropagation();
                          onNavigate?.(item.actionUrl);
                        }}
                        sx={{ textTransform: 'none', minHeight: 28, px: 1, fontSize: '0.72rem' }}
                      >
                        {item.actionLabel || '열기'}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {items.length > 0 && !listSummary && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1, px: 1, py: 0.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}
        >
          💡 항목을 선택하면 상세 정보를 확인할 수 있습니다.
        </Typography>
      )}

      {listSummary && (
        <BotListFooter
          listSummary={listSummary}
          shownCount={items.length}
          onNavigateRoute={onNavigateRoute}
          onFollowUp={onFollowUp}
        />
      )}
    </Box>
  );
};

// 외부 자료 리서치 뷰 - responseType: WEB_RESEARCH
// LLM 요약문은 "### 제목" / "1. 항목" 정도의 마크다운으로 온다. 마크다운 라이브러리를 새로 들이지
// 않고, 그 두 형태만 화면 요소로 바꾼다(2026-10-05: '###'가 글자 그대로 보였다).
// "출처" 절은 아래 출처 표와 같은 내용이라 본문에서는 뺀다(두 번 나오던 문제).
const renderResearchSummary = (summary) => {
  const blocks = [];
  let skipping = false;
  `${summary || ''}`.split('\n').forEach((rawLine, index) => {
    const line = rawLine.trim();
    // 제목은 "### 핵심 요약" 말고 "**핵심 요약**" 한 줄로도 온다(운영 qwen3-4b-instruct, 2026-10-05) -
    // 별표가 화면에 그대로 보였고 "**출처 번호 표시**" 섹션도 걸러지지 않았다.
    const heading = line.match(/^#{1,6}\s*(.+)$/) || line.match(/^\*\*(.+?)\*\*\s*:?$/);
    if (heading) {
      skipping = /출처|참고\s*자료|sources?/i.test(heading[1]);
      if (!skipping) {
        blocks.push(
          <Typography key={`h-${index}`} variant="subtitle2" sx={{ fontWeight: 'bold', mt: blocks.length ? 1 : 0 }}>
            {heading[1]}
          </Typography>
        );
      }
      return;
    }
    // "[2], [3], [4]"처럼 출처 번호만 있는 줄은 아래 출처 표와 겹친다.
    if (skipping || !line || /^(\[\d+\][,\s]*)+$/.test(line)) return;
    blocks.push(
      <Typography key={`p-${index}`} variant="body2" sx={{ pl: /^(\d+\.|[-*])\s/.test(line) ? 1 : 0 }}>
        {line.replace(/^[-*]\s/, '• ').replace(/\*\*(.+?)\*\*/g, '$1')}
      </Typography>
    );
  });
  return blocks;
};

const WebResearchView = ({ data, message }) => {
  const [showDetails, setShowDetails] = useState(false);
  const sources = Array.isArray(data?.sources) ? data.sources : [];
  const status = data?.status || 'OK';
  const summary = data?.summary || message || '';
  const severity = status === 'OK' ? 'info' : status === 'BLOCKED' ? 'warning' : 'warning';
  const severityColor = severity === 'info' ? 'info.main' : 'warning.main';
  const fetchedCount = data?.contentFetchedCount ?? sources.filter((source) => source.contentFetched).length;
  // 출처에 날짜가 하나도 없으면 날짜 칸을 아예 뺀다 - 전부 '-'인 칸은 정보가 없다(검색 서비스가
  // 날짜를 주지 않는 경우가 대부분이다).
  const hasAnyDate = sources.some((source) => source.publishedAt);
  // 검색 엔진·소요시간·정책·감사 ID 는 운영 확인용이라 기본은 접어 둔다(사용자에게 'provider ddgs ·
  // 16172ms' 같은 줄이 그대로 보였다).
  const hasDetails = Boolean(data?.internalSummary || data?.provider || data?.latencyMs !== undefined
    || data?.auditId || data?.policySummary);

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 2, p: 1.25, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #dfe3e8', borderLeft: 4, borderLeftColor: severityColor }}>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            외부 자료 · {data?.query ? `"${data.query}"` : '검색'} · 출처 {data?.sourceCount ?? sources.length}건
            {fetchedCount > 0 ? ` (본문 확인 ${fetchedCount}건)` : ''}
          </Typography>
          {renderResearchSummary(summary)}
          {hasDetails && (
            <Box>
              <Button size="small" onClick={() => setShowDetails((prev) => !prev)} sx={{ px: 0, minWidth: 0 }}>
                {showDetails ? '검색 정보 접기' : '검색 정보'}
              </Button>
              <Collapse in={showDetails}>
                <Stack spacing={0.25} sx={{ pt: 0.25 }}>
                  {data?.internalSummary && (
                    <Typography variant="caption" color="text.secondary">내부 기준: {data.internalSummary}</Typography>
                  )}
                  {(data?.provider || data?.latencyMs !== undefined) && (
                    <Typography variant="caption" color="text.secondary">
                      검색 엔진 {data.provider || '-'} · {data.latencyMs ?? '-'}ms
                    </Typography>
                  )}
                  {data?.policySummary && (
                    <Typography variant="caption" color="text.secondary">{data.policySummary}</Typography>
                  )}
                  {data?.auditId && (
                    <Typography variant="caption" color="text.secondary">감사 ID: {data.auditId}</Typography>
                  )}
                </Stack>
              </Collapse>
            </Box>
          )}
        </Stack>
      </Box>

      {sources.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            출처 ({sources.length}건)
          </Typography>
          {/* 컴팩트 테이블 - 제목(굵게) · 출처명/구분(작게) · 날짜. 본문 발췌와 "출처 열기"는
              정보를 지우지 않고 행을 눌러 펼치는 상세 영역으로 옮겼다(rank 번호·공식/본문확인
              표시도 코드성 정보라 작은 보조 텍스트로 내렸다). */}
          <CompactListTable
            items={sources}
            getRowKey={(source, index) => source.url || index}
            getExpandedContent={(source) => (
              <Stack spacing={1}>
                {source.snippet && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {source.snippet}
                  </Typography>
                )}
                {source.contentSnippet && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block', mb: 0.25 }}>
                      본문 발췌
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {source.contentSnippet}
                    </Typography>
                  </Box>
                )}
                {source.url && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LaunchIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      window.open(source.url, '_blank', 'noopener,noreferrer');
                    }}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  >
                    출처 열기
                  </Button>
                )}
              </Stack>
            )}
            columns={[
              {
                key: 'title',
                header: '출처',
                width: hasAnyDate ? '70%' : '100%',
                render: (source) => {
                  const badges = [source.official && '공식', source.contentFetched && '본문확인'].filter(Boolean);
                  const subtitleParts = [source.sourceName, ...badges].filter(Boolean);
                  return renderTitleCell(source.title || source.url, subtitleParts.join(' · '), '제목 없음');
                },
              },
              ...(hasAnyDate ? [{
                key: 'date',
                header: '날짜',
                width: '30%',
                align: 'right',
                render: (source) => (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {source.publishedAt || '-'}
                  </Typography>
                ),
              }] : []),
            ]}
          />
        </Stack>
      )}

      {data?.notice && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}
        >
          {data.notice}
        </Typography>
      )}
    </Box>
  );
};

// NCG CAM 매뉴얼 Q&A 뷰 - responseType: MANUAL_QA
// 웹 리서치와 같은 모양(요약 → 출처 표)이지만 출처가 링크가 아니라 매뉴얼 페이지다. 행을 누르면
// 근거가 된 매뉴얼 발췌가 펼쳐진다 - 답이 맞는지 현장에서 바로 대조할 수 있게.
const ManualQaView = ({ data, message }) => {
  const sources = Array.isArray(data?.sources) ? data.sources : [];
  const status = data?.status || 'OK';
  const summary = data?.summary || message || '';
  const accent = status === 'OK' ? 'info.main' : 'warning.main';

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 2, p: 1.25, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #dfe3e8', borderLeft: 4, borderLeftColor: accent }}>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {data?.manual || 'NCG CAM'} 매뉴얼{sources.length > 0 ? ` · 근거 ${sources.length}페이지` : ''}
          </Typography>
          {renderResearchSummary(summary)}
        </Stack>
      </Box>

      {sources.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            매뉴얼 출처
          </Typography>
          <CompactListTable
            items={sources}
            getRowKey={(source, index) => `${source.file || ''}-${index}`}
            getExpandedContent={(source) => (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line', display: 'block' }}>
                {source.text}
              </Typography>
            )}
            columns={[
              {
                key: 'title',
                header: '페이지',
                width: '100%',
                render: (source) => {
                  const parts = (source.path || '').split(' > ');
                  const section = parts.slice(0, -1).join(' > ');
                  const lang = source.lang === 'en' ? '영문판' : null;
                  return renderTitleCell(
                    `[${source.index}] ${source.title || parts[parts.length - 1] || '제목 없음'}`,
                    [section, lang].filter(Boolean).join(' · '),
                    '제목 없음',
                  );
                },
              },
            ]}
          />
        </Stack>
      )}
    </Box>
  );
};

// 공구 사양 / 가공 조건 검색 뷰 - responseType: MACHINING_RAG
const MachiningRagView = ({ data, message }) => {
  const results = Array.isArray(data?.results) ? data.results : [];
  const status = data?.status || (results.length > 0 ? 'OK' : 'EMPTY');
  const summary = data?.summary || message || '';
  const severity = status === 'OK' ? 'info' : status === 'NEED_MORE_DETAIL' ? 'warning' : 'warning';

  const value = (...items) => items.find((item) => item !== undefined && item !== null && `${item}`.trim() !== '');
  const specText = (item) => value(item.toolSpec, item.toolName, [
    item.toolD ? `D ${item.toolD}` : null,
    item.toolR ? `R ${item.toolR}` : null,
  ].filter(Boolean).join(' / '));
  const speedText = (item) => value(item.feed, item.feeder);

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(720px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            공구 사양 검색
          </Typography>
          {data?.query && (
            <Typography variant="caption" color="text.secondary">
              검색어: {data.query}
            </Typography>
          )}
          {/* 요약 글이 "1. 파일(시트 26행) 프로그램: … / 공구번호 … / RPM …"을 결과마다 늘어놓아 아래 표와
              겹치고 읽기 어려웠다(2026-10-08). 결과가 있으면 건수·프로그램 한 줄만, 자세한 값은 아래 표가 맡는다. */}
          {results.length > 0 ? (
            <Typography variant="body2">
              공구 사양 <b>{results.length}건</b>
              {(() => {
                const programs = [...new Set(results.map((r) => value(r.programName, r.modelName)).filter(Boolean))];
                return programs.length ? ` · ${programs.slice(0, 2).join(', ')}${programs.length > 2 ? ` 외 ${programs.length - 2}` : ''}` : '';
              })()}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                행을 누르면 여유·SIZE·소재·원본 행이 펼쳐집니다.
              </Typography>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {summary}
            </Typography>
          )}
          {results.length === 0 && (data?.indexedDocumentCount !== undefined || data?.indexedRowCount !== undefined) && (
            <Typography variant="caption" color="text.secondary">
              인덱스: 파일 {data.indexedDocumentCount ?? 0}개 · 행 {data.indexedRowCount ?? 0}개
            </Typography>
          )}
        </Stack>
      </Alert>

      {results.length > 0 && (
        // 컴팩트 테이블 - 프로그램명(핵심)을 굵게, 파일/시트/행 번호는 작은 보조 텍스트로 내린다.
        // 순위(#rank)·여유/SIZE/소재/비고·원본 행은 정보 손실 없이 행을 눌러 펼치는 상세로 옮겼다.
        <CompactListTable
          items={results}
          getRowKey={(item, index) => `${item.sourcePath || item.fileName}-${item.sheetName || ''}-${item.rowNo || index}`}
          getExpandedContent={(item) => {
            const metaLine = [
              item.allowance ? `여유 ${item.allowance}` : null,
              item.size ? `SIZE ${item.size}` : null,
              item.material ? `소재 ${item.material}` : null,
              item.remark ? `비고 ${item.remark}` : null,
            ].filter(Boolean).join(' · ');
            if (!metaLine && !item.rawText) return null;
            return (
              <Stack spacing={0.75}>
                {metaLine && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {metaLine}
                  </Typography>
                )}
                {item.rawText && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block', mb: 0.25 }}>
                      원본 행
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                      {item.rawText}
                    </Typography>
                  </Box>
                )}
              </Stack>
            );
          }}
          columns={[
            ...(results.some((item) => item.thumbnailPath) ? [{
              key: 'thumb',
              header: '',
              width: 56,
              render: (item) => (item.thumbnailPath ? <RagThumbnail path={item.thumbnailPath} size={44} /> : null),
            }] : []),
            {
              key: 'title',
              header: '프로그램',
              width: '38%',
              render: (item) => (
                <>
                  {renderTitleCell(value(item.programName, item.modelName, item.workName), null, '프로그램명 없음')}
                  {/* 파일 이름을 누르면 원본 엑셀을 받는다(2026-10-10 사용자 요청) */}
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    <RagSourceLink path={item.sourcePath} label={item.fileName || item.sourcePath} />
                    {[item.sheetName, item.rowNo ? `${item.rowNo}행` : null].filter(Boolean).map((t) => ` · ${t}`).join('')}
                  </Typography>
                </>
              ),
            },
            {
              key: 'spec',
              header: '공구규격',
              width: '28%',
              render: (item) => (
                <Typography variant="caption" color="text.secondary">
                  {[item.toolNo ? `#${item.toolNo}` : null, specText(item), item.toolLength ? `길이 ${item.toolLength}` : null, item.pitch ? `Pitch ${item.pitch}` : null].filter(Boolean).join(' · ') || '-'}
                </Typography>
              ),
            },
            {
              key: 'cond',
              header: '가공조건',
              width: '26%',
              render: (item) => (
                <Typography variant="caption" color="text.secondary">
                  {[item.rpm ? `RPM ${item.rpm}` : null, speedText(item) ? `Feed ${speedText(item)}` : null, item.minZ ? `MinZ ${item.minZ}` : null].filter(Boolean).join(' · ') || '-'}
                </Typography>
              ),
            },
          ]}
        />
      )}

      {data?.notice && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1, p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}
        >
          {data.notice}
        </Typography>
      )}
    </Box>
  );
};

// 공구 레시피(도구-중심) 조회 뷰 - responseType: TOOL_RECIPE
// mode=catalog(공구 카탈로그) | tool(공구번호별 레시피) | region(가공 영역별 조건)
const ToolRecipeView = ({ data, message }) => {
  const results = Array.isArray(data?.results) ? data.results : [];
  const mode = data?.mode || 'catalog';
  const status = data?.status || (results.length > 0 ? 'OK' : 'EMPTY');
  const summary = data?.summary || message || '';
  const severity = status === 'OK' ? 'info' : 'warning';

  const title = mode === 'tool'
    ? '공구 레시피'
    : mode === 'region'
      ? '영역별 가공조건'
      : '공구 카탈로그';

  const has = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';
  // 백엔드 DTO의 zPitch는 JSON에서 zpitch로 직렬화된다.
  const zPitchOf = (item) => (has(item.zpitch) ? item.zpitch : item.zPitch);
  const specText = (item) => [
    has(item.toolD) ? `D${item.toolD}` : null,
    has(item.toolR) ? `R${item.toolR}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(720px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          {data?.query && (
            <Typography variant="caption" color="text.secondary">
              검색어: {data.query}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {summary}
          </Typography>
          {data?.notice && (
            <Typography variant="caption" color="text.secondary">
              {data.notice}
            </Typography>
          )}
        </Stack>
      </Alert>

      {results.length > 0 && (
        <Stack spacing={1}>
          {results.map((item, index) => (
            <Card
              key={`${item.toolNo || 'tool'}-${item.toolD || ''}-${item.toolR || ''}-${item.region || ''}-${item.rank || index}`}
              sx={{ border: '1px solid #dfe3e8', borderRadius: 1 }}
            >
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack spacing={0.9}>
                  {/* 타이디 카드: 공구/규격(핵심)을 굵게 키우고, 발생건수는 보조 배지로 오른쪽에 */}
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {has(item.toolNo) ? `${item.toolNo}번 공구` : '공구'}
                      {specText(item) ? ` · ${specText(item)}` : ''}
                    </Typography>
                    {has(item.occurrenceCount) && mode === 'catalog' && (
                      <Chip label={`${item.occurrenceCount}건`} size="small" color="default" variant="outlined" />
                    )}
                  </Stack>

                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    {has(item.region) && <Chip label={item.region} size="small" color="info" variant="outlined" />}
                    {has(item.stage) && <Chip label={`${item.stage}삭`} size="small" variant="outlined" />}
                    {has(item.rpm) && <Chip label={`RPM ${item.rpm}`} size="small" variant="outlined" />}
                    {has(item.feed) && <Chip label={`Feed ${item.feed}`} size="small" variant="outlined" />}
                    {has(item.xyPitch) && <Chip label={`XY피치 ${item.xyPitch}`} size="small" variant="outlined" />}
                    {has(zPitchOf(item)) && <Chip label={`Z피치 ${zPitchOf(item)}`} size="small" variant="outlined" />}
                    {has(item.toolpath) && <Chip label={item.toolpath} size="small" variant="outlined" />}
                  </Stack>

                  {(has(item.horizontalAllowance) || has(item.verticalAllowance)) && (
                    <Typography variant="caption" color="text.secondary">
                      {[
                        has(item.horizontalAllowance) ? `수평여유 ${item.horizontalAllowance}` : null,
                        has(item.verticalAllowance) ? `수직여유 ${item.verticalAllowance}` : null,
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// 이탈 판정(DeviationPolicy) verdict -> 배지 라벨/색상. ISSUE=문제(빨강), WARN=주의(주황),
// OK=적합(초록), 그 외(NO_STANDARD/없음)=기준없음(회색).
// TOOL_USAGE(공구별 실적 조회)와 TOOLSHEET_REVIEW(업로드 툴시트 검토) 양쪽 뷰가 공유한다.
const verdictMeta = (verdict) => {
  switch (verdict) {
    case 'ISSUE':
      return { label: '문제', color: 'error' };
    case 'WARN':
      return { label: '주의', color: 'warning' };
    case 'OK':
      return { label: '적합', color: 'success' };
    default:
      return { label: '기준없음', color: 'default' };
  }
};

const hasValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';

const formatNumberValue = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? `${num}` : null;
};

// 공구별 실적(과거 실제 가공 조건) 조회 뷰 - responseType: TOOL_USAGE
// 기준자료(표준 조건, TOOL_RECIPE)와 코어 가공 실적(실제 사용 조건)을 나란히 비교해 편차까지 보여준다.
// 규격별(D,R,툴패스) 기준-실적 비교 테이블의 RPM/Feed 셀 - 실적 중앙값(굵게)과 기준값+판정배지(작게)를
// 2행으로 보여준다. ToolUsageResponseDto.specComparisons 1건의 RPM 또는 Feed.
const renderUsageMetricCell = (standardValue, min, median, max, metricMeta) => {
  const has = hasValue;
  const formatNumber = formatNumberValue;
  if (!has(standardValue) && !has(median)) {
    return <Typography variant="caption" color="text.secondary">-</Typography>;
  }
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 'medium' }} noWrap>
        {has(median) ? `${formatNumber(median)} (${formatNumber(min) ?? '-'}~${formatNumber(max) ?? '-'})` : '실적없음'}
      </Typography>
      <Stack direction="row" spacing={0.4} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" noWrap>
          기준 {formatNumber(standardValue) ?? '없음'}
        </Typography>
        {metricMeta.color !== 'default' && (
          <Chip label={metricMeta.label} size="small" color={metricMeta.color} sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
        )}
      </Stack>
    </>
  );
};

// 개인 표시 필터(판정 자체는 조직 정책으로 고정 — 개인은 "보이는 카드"만 바꾼다). localStorage에만 저장.
// TOOL_USAGE(공구별 실적 조회)와 TOOLSHEET_REVIEW(업로드 툴시트 검토) 양쪽 뷰가 그대로 재사용한다.
const VERDICT_FILTER_STORAGE_KEY = 'rag.verdictFilter';
const VERDICT_FILTER_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'WARN_UP', label: '주의까지' },
  { value: 'ISSUE_ONLY', label: '문제만' },
];
const DEFAULT_VERDICT_FILTER = 'WARN_UP';

const matchesVerdictFilter = (verdict, filter) => {
  if (filter === 'ALL') return true;
  if (filter === 'ISSUE_ONLY') return verdict === 'ISSUE';
  return verdict === 'ISSUE' || verdict === 'WARN'; // WARN_UP: 주의 이상(주의+문제)
};

const readStoredVerdictFilter = () => {
  try {
    const stored = window.localStorage.getItem(VERDICT_FILTER_STORAGE_KEY);
    return VERDICT_FILTER_OPTIONS.some((option) => option.value === stored)
      ? stored
      : DEFAULT_VERDICT_FILTER;
  } catch (error) {
    return DEFAULT_VERDICT_FILTER;
  }
};

// 개인 표시 필터 상태 훅. 판정(verdict) 자체는 바꾸지 않고 "어떤 카드를 보여줄지"만 로컬에 기억한다.
const useVerdictFilter = () => {
  const [verdictFilter, setVerdictFilter] = useState(readStoredVerdictFilter);

  const handleVerdictFilterChange = (event, nextValue) => {
    if (!nextValue) return; // 토글 그룹에서 선택 해제로 빈 값이 되는 것은 무시(항상 하나는 선택 유지)
    setVerdictFilter(nextValue);
    try {
      window.localStorage.setItem(VERDICT_FILTER_STORAGE_KEY, nextValue);
    } catch (error) {
      // localStorage 접근 불가(프라이빗 모드 등) - 이번 세션에서만 필터가 적용된다.
    }
  };

  return [verdictFilter, handleVerdictFilterChange];
};

// 필터 ToggleButtonGroup + 숨김 건수 caption. TOOL_USAGE/TOOLSHEET_REVIEW 양쪽에서 재사용.
const VerdictFilterToggle = ({ value, onChange, totalCount, visibleCount }) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    <ToggleButtonGroup size="small" value={value} exclusive onChange={onChange}>
      {VERDICT_FILTER_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
    {visibleCount < totalCount && (
      <Typography variant="caption" color="text.secondary">
        {totalCount - visibleCount}건 숨김(필터: {VERDICT_FILTER_OPTIONS.find((option) => option.value === value)?.label})
      </Typography>
    )}
  </Stack>
);

const ToolUsageView = ({ data, message }) => {
  const [verdictFilter, handleVerdictFilterChange] = useVerdictFilter();
  const [showDetails, setShowDetails] = useState(false);

  const status = data?.status || 'EMPTY';
  const summary = data?.summary || message || '';
  const specComparisons = Array.isArray(data?.specComparisons) ? data.specComparisons : [];
  // 필터는 카드 노출 여부만 바꾼다 - 판정 자체(verdict)나 헤더의 문제/주의/적합 집계는 항상 전체 기준.
  const filteredSpecComparisons = specComparisons.filter((item) => matchesVerdictFilter(item.verdict, verdictFilter));
  const severity = data?.verdict === 'ISSUE' ? 'error' : status === 'OK' ? 'info' : 'warning';
  const usage = data?.usage;
  const standardRows = Array.isArray(data?.standardRows) ? data.standardRows : [];
  const deviations = Array.isArray(data?.deviations) ? data.deviations : [];

  const has = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';
  const verdictCountTotal = (data?.issueCount || 0) + (data?.warnCount || 0) + (data?.okCount || 0) + (data?.noStandardCount || 0);

  // 표준자료 행들에서 RPM/Feed 대표값(중앙값)을 프론트에서 계산해 실적과 나란히 비교한다.
  // (백엔드 deviations 문장에는 포함돼 있지만, 표 형태 비교를 위해 별도로 뽑아낸다)
  const medianOfStandard = (field) => {
    const values = standardRows
      .map((row) => Number.parseFloat(row?.[field]))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);
    if (values.length === 0) return null;
    const mid = Math.floor(values.length / 2);
    return values.length % 2 === 0 ? (values[mid - 1] + values[mid]) / 2 : values[mid];
  };

  const deviationFor = (label) => deviations.find((line) => line.startsWith(`실적 ${label}`));

  const formatNumber = (value) => {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isFinite(num) ? `${num}` : null;
  };

  const renderComparisonRow = (label, standardValue, min, median, max) => {
    const standardText = formatNumber(standardValue);
    const medianText = formatNumber(median);
    if (!standardText && !medianText) return null;
    const deviation = deviationFor(label);
    return (
      <Stack key={label} direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: 48 }}>{label}</Typography>
        <Chip
          label={`기준값 ${standardText ?? '없음'}`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={medianText ? `실적 중앙값 ${medianText} (${formatNumber(min) ?? '-'}~${formatNumber(max) ?? '-'})` : '실적 없음'}
          size="small"
          variant="outlined"
          color="primary"
        />
        {deviation && <Chip label={deviation} size="small" color="warning" />}
      </Stack>
    );
  };

  // 머리글은 한 줄 + 판정 집계 칩만 바로 보여주고, 나머지 설명(규격 매칭 근거·중앙값·인덱스 행 수)은
  // "자세히"로 접는다 - 전체를 빨간 Alert 로 펼쳐 두니 답의 첫 화면이 설명문으로 꽉 차서 정작
  // 아래 규격 비교표가 안 보였다(2026-10-05 사용자 피드백: 봇 답이 너무 길다).
  const summaryLines = `${summary}`.split('\n').map((line) => line.trim()).filter(Boolean);
  const headline = summaryLines[0] || '공구 실적 조회';
  const restLines = summaryLines.slice(1);
  const hasDetails = restLines.length > 0 || has(data?.joinBasis) || Boolean(data?.notice);
  const severityColor = { error: 'error.main', warning: 'warning.main', info: 'info.main' }[severity];

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(720px, 100%)' }, maxWidth: '100%' }}>
      <Box sx={{ mb: 1.5, p: 1.25, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #dfe3e8', borderLeft: 4, borderLeftColor: severityColor }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {headline}
          </Typography>
          {verdictCountTotal > 0 && (
            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
              {data.issueCount > 0 && <Chip label={`문제 ${data.issueCount}`} size="small" color="error" />}
              {data.warnCount > 0 && <Chip label={`주의 ${data.warnCount}`} size="small" color="warning" />}
              {data.okCount > 0 && <Chip label={`적합 ${data.okCount}`} size="small" color="success" />}
              {data.noStandardCount > 0 && <Chip label={`기준없음 ${data.noStandardCount}`} size="small" variant="outlined" />}
            </Stack>
          )}
          {hasDetails && (
            <Box>
              <Button size="small" onClick={() => setShowDetails((prev) => !prev)} sx={{ px: 0, minWidth: 0 }}>
                {showDetails ? '설명 접기' : '자세히'}
              </Button>
              <Collapse in={showDetails}>
                <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                  {restLines.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {restLines.join('\n')}
                    </Typography>
                  )}
                  {has(data?.joinBasis) && (
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }} color="text.secondary">
                      {data.joinBasis}
                    </Typography>
                  )}
                  {data?.notice && (
                    <Typography variant="caption" color="text.secondary">
                      {data.notice}
                    </Typography>
                  )}
                </Stack>
              </Collapse>
            </Box>
          )}
        </Stack>
      </Box>

      {specComparisons.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <VerdictFilterToggle
            value={verdictFilter}
            onChange={handleVerdictFilterChange}
            totalCount={specComparisons.length}
            visibleCount={filteredSpecComparisons.length}
          />
          {/* 컴팩트 테이블 - 규격이 핵심이라 굵게, 판정은 상태 배지(초록/주황/빨강/중립)로 오른쪽에.
              실적 공구번호 목록·판정 기준 문구·편차 설명은 정보 손실 없이 행을 눌러 펼치는 상세로 옮겼다. */}
          <CompactListTable
            items={filteredSpecComparisons}
            getRowKey={(item, index) => `${item.spec || 'spec'}-${item.toolpath || ''}-${index}`}
            getExpandedContent={(item) => {
              const hasDetail = hasValue(item.policyLabel)
                || (Array.isArray(item.toolNoDistribution) && item.toolNoDistribution.length > 0)
                || (Array.isArray(item.deviations) && item.deviations.length > 0);
              if (!hasDetail) return null;
              return (
                <Stack spacing={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    실적 {item.usageCount ?? 0}건 · 작업 파일 {item.usageFileCount ?? 0}개
                    {hasValue(item.policyLabel) ? ` · 판정 기준: ${item.policyLabel}` : ''}
                  </Typography>
                  {Array.isArray(item.toolNoDistribution) && item.toolNoDistribution.length > 0 && (
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap alignItems="center">
                      <Typography variant="caption" color="text.secondary">실적 공구번호</Typography>
                      {item.toolNoDistribution.map((entry) => (
                        <Chip key={entry} label={entry} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  )}
                  {Array.isArray(item.deviations) && item.deviations.length > 0 && (
                    <Stack spacing={0.3}>
                      {item.deviations.map((line) => (
                        <Typography key={line} variant="caption" color="warning.main">{line}</Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
              );
            }}
            columns={[
              {
                key: 'spec',
                header: '규격',
                width: '32%',
                render: (item) => renderTitleCell(
                  [item.spec, item.toolpath].filter((part) => hasValue(part)).join(' · '),
                  [item.toolShape, [item.region, item.stage].filter((part) => hasValue(part)).join('/')].filter(Boolean).join(' · ')
                ),
              },
              {
                key: 'verdict',
                header: '판정',
                width: '14%',
                render: (item) => {
                  const meta = verdictMeta(item.verdict);
                  return renderStageCell(meta.label, meta.color, meta.color === 'default' ? 'outlined' : 'filled');
                },
              },
              {
                key: 'rpm',
                header: 'RPM',
                width: '27%',
                render: (item) => renderUsageMetricCell(item.standardRpm, item.rpmMin, item.rpmMedian, item.rpmMax, verdictMeta(item.rpmVerdict)),
              },
              {
                key: 'feed',
                header: 'Feed',
                width: '27%',
                render: (item) => renderUsageMetricCell(item.standardFeed, item.feedMin, item.feedMedian, item.feedMax, verdictMeta(item.feedVerdict)),
              },
            ]}
          />
        </Stack>
      )}

      {status === 'EMPTY' ? null : usage && (
        <Card sx={{ border: '1px solid #dfe3e8', borderRadius: 1, mb: 1.5 }}>
          <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                전체 매칭 요약 · 실적 {usage.useCount ?? 0}건
                {has(usage.fileCount) ? ` · 작업 파일 ${usage.fileCount}개` : ''}
              </Typography>

              <Stack spacing={0.25}>
                {renderComparisonRow('RPM', medianOfStandard('rpm'), usage.rpmMin, usage.rpmMedian, usage.rpmMax)}
                {renderComparisonRow('Feed', medianOfStandard('feed'), usage.feedMin, usage.feedMedian, usage.feedMax)}
              </Stack>

              {Array.isArray(usage.matchedSpecs) && usage.matchedSpecs.length > 0 && (
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" color="text.secondary">매칭 규격</Typography>
                  {usage.matchedSpecs.map((spec) => (
                    <Chip key={spec} label={spec} size="small" variant="outlined" color="secondary" />
                  ))}
                </Stack>
              )}

              {Array.isArray(usage.toolSpecs) && usage.toolSpecs.length > 0 && (
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" color="text.secondary">공구 규격(D·R)</Typography>
                  {usage.toolSpecs.map((spec) => (
                    <Chip key={spec} label={spec} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}

              {Array.isArray(usage.toolNoDistribution) && usage.toolNoDistribution.length > 0 && (
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" color="text.secondary">실적 공구번호</Typography>
                  {usage.toolNoDistribution.map((entry) => (
                    <Chip key={entry} label={entry} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}

              {Array.isArray(usage.topWorkNames) && usage.topWorkNames.length > 0 && (
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" color="text.secondary">작업/툴패스</Typography>
                  {usage.topWorkNames.map((work) => (
                    <Chip key={work} label={work} size="small" variant="outlined" color="info" />
                  ))}
                </Stack>
              )}

              {Array.isArray(usage.sampleFiles) && usage.sampleFiles.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  대표 파일: {usage.sampleFiles.join(', ')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {standardRows.length > 0 && (
        <Accordion disableGutters sx={{ border: '1px solid #dfe3e8', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">기준자료 표준 조건 ({standardRows.length}건)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <ToolRecipeView
              data={{ results: standardRows, mode: 'tool', status: 'OK', summary: '', notice: null }}
              message=""
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

// 업로드 툴시트 검토 행 카드 - ToolSheetReviewResponseDto.rows 1건.
const ToolSheetReviewRowCard = ({ row }) => {
  const meta = verdictMeta(row.verdict);
  const rpmMeta = verdictMeta(row.rpmVerdict);
  const feedMeta = verdictMeta(row.feedVerdict);

  const headerParts = [];
  if (hasValue(row.toolNo)) headerParts.push(`${row.toolNo}번`);
  const spec = [
    hasValue(row.toolD) ? `D${row.toolD}` : null,
    hasValue(row.toolR) && row.toolR !== '0' ? `R${row.toolR}` : null,
  ].filter(Boolean).join(' · ');
  if (spec) headerParts.push(spec);
  if (hasValue(row.toolShape)) headerParts.push(row.toolShape);
  if (hasValue(row.toolpath)) headerParts.push(row.toolpath);
  const header = headerParts.length > 0 ? headerParts.join(' · ') : `${row.rowNo ?? '?'}행`;
  const tagParts = [row.standardRegion, row.standardStage].filter((part) => hasValue(part));

  const renderMetricRow = (label, actual, standard, historyMedian, historyCount, metricMeta) => {
    if (!hasValue(actual) && !hasValue(standard) && !hasValue(historyMedian)) return null;
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap key={label}>
        <Typography variant="body2" sx={{ minWidth: 40 }}>{label}</Typography>
        <Chip label={`실측 ${hasValue(actual) ? actual : '없음'}`} size="small" variant="outlined" color="primary" />
        <Chip label={`기준 ${formatNumberValue(standard) ?? '없음'}`} size="small" variant="outlined" />
        {hasValue(historyMedian) && (
          <Chip
            label={`현장 중앙값 ${formatNumberValue(historyMedian)}${historyCount ? ` (${historyCount}건)` : ''}`}
            size="small"
            variant="outlined"
            color="secondary"
          />
        )}
        {metricMeta.color !== 'default' && <Chip label={metricMeta.label} size="small" color={metricMeta.color} />}
      </Stack>
    );
  };

  return (
    <Card sx={{ border: '1px solid #dfe3e8', borderRadius: 1 }}>
      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack spacing={0.9}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.6} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {header}{tagParts.length > 0 ? ` (${tagParts.join('/')})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">{row.rowNo}행</Typography>
            </Stack>
            <Chip label={meta.label} size="small" color={meta.color} variant={meta.color === 'default' ? 'outlined' : 'filled'} />
          </Stack>

          <Stack spacing={0.4}>
            {renderMetricRow('RPM', row.rpm, row.standardRpm, row.historyRpmMedian, row.historyCount, rpmMeta)}
            {renderMetricRow('Feed', row.feed, row.standardFeed, row.historyFeedMedian, row.historyCount, feedMeta)}
          </Stack>

          {hasValue(row.policyLabel) && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              판정 기준: {row.policyLabel}
              {/* 기준자료는 같은 공구·툴패스에 표준값이 여러 개인 경우가 있어, 그중 가장 근접한 기준과 비교한다. */}
              {row.standardCandidateCount > 1 && ` · 기준 후보 ${row.standardCandidateCount}종 중 근접 기준`}
            </Typography>
          )}

          {Array.isArray(row.messages) && row.messages.length > 0 && (
            <Stack spacing={0.3}>
              {row.messages.map((line) => (
                <Typography key={line} variant="caption" color="warning.main">{line}</Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// verdict 정렬 우선순위: 문제 → 주의 → 기준없음 → 적합.
const TOOLSHEET_REVIEW_VERDICT_ORDER = { ISSUE: 0, WARN: 1, NO_STANDARD: 2, OK: 3 };

// 업로드 툴시트 검토 뷰 - responseType: TOOLSHEET_REVIEW
const ToolSheetReviewView = ({ data, message }) => {
  const [verdictFilter, handleVerdictFilterChange] = useVerdictFilter();

  const summary = data?.summary || message || '';
  const verdictCountTotal = (data?.issueCount || 0) + (data?.warnCount || 0) + (data?.okCount || 0) + (data?.noStandardCount || 0);

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const severity = data?.verdict === 'ISSUE' ? 'error' : (rows.length === 0 ? 'warning' : 'info');
  const sortedRows = [...rows].sort((a, b) => {
    const rank = (TOOLSHEET_REVIEW_VERDICT_ORDER[a.verdict] ?? 4) - (TOOLSHEET_REVIEW_VERDICT_ORDER[b.verdict] ?? 4);
    return rank !== 0 ? rank : (a.rowNo ?? 0) - (b.rowNo ?? 0);
  });
  const filteredRows = sortedRows.filter((row) => matchesVerdictFilter(row.verdict, verdictFilter));

  const showAiExplanation = hasValue(data?.aiExplanation) && data?.aiExplanationStatus === 'OK';

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(720px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            가공조건 검토{hasValue(data?.fileName) ? ` · ${data.fileName}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {summary}
          </Typography>
          {verdictCountTotal > 0 && (
            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
              {data.issueCount > 0 && <Chip label={`문제 ${data.issueCount}`} size="small" color="error" />}
              {data.warnCount > 0 && <Chip label={`주의 ${data.warnCount}`} size="small" color="warning" />}
              {data.okCount > 0 && <Chip label={`적합 ${data.okCount}`} size="small" color="success" />}
              {data.noStandardCount > 0 && <Chip label={`기준없음 ${data.noStandardCount}`} size="small" variant="outlined" />}
            </Stack>
          )}
          {data?.skippedUncalculated > 0 && (
            <Typography variant="caption" color="text.secondary">
              미연산 {data.skippedUncalculated}행은 검토 대상에서 제외했습니다.
            </Typography>
          )}
        </Stack>
      </Alert>

      {showAiExplanation && (
        <Alert severity="info" icon={false} sx={{ mb: 1.5, bgcolor: 'action.hover' }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              🤖 AI 해석 (참고용)
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {data.aiExplanation}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              판정과 수치는 기준자료 대조 결과이며, 이 문단은 해석입니다.
            </Typography>
          </Stack>
        </Alert>
      )}

      {rows.length > 0 && (
        <Stack spacing={1}>
          <VerdictFilterToggle
            value={verdictFilter}
            onChange={handleVerdictFilterChange}
            totalCount={rows.length}
            visibleCount={filteredRows.length}
          />
          {filteredRows.map((row, index) => (
            <ToolSheetReviewRowCard key={`${row.rowNo ?? index}-${row.toolD ?? ''}-${row.toolR ?? ''}`} row={row} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

// 가공조건 입력값 검증 뷰(스펙 a) - responseType: TOOL_CONDITION_CHECK
// 채팅 문장에 RPM/Feed 등 실측값이 실려 있으면(예: "D6 RPM 5000 Feed 300 괜찮아?") 즉시 기준자료와
// 대조해 판정한다. 판정 카드는 TOOLSHEET_REVIEW(업로드 검토)와 완전히 동일한 ToolSheetReviewRowCard를
// 그대로 재사용한다 — 판정 로직도 백엔드에서 reviewRow를 공유하고, 표시 방식도 중복 구현하지 않는다.
const ToolConditionCheckView = ({ data, message }) => {
  const status = data?.status || 'OK';
  const parsedSummary = data?.parsedSummary;
  const summary = data?.summary || message || '';
  const row = data?.row;
  const guidance = data?.guidance;
  const showAiExplanation = hasValue(data?.aiExplanation) && data?.aiExplanationStatus === 'OK';

  const severity = status === 'NEED_MORE_INPUT'
    ? 'info'
    : status === 'DISABLED'
      ? 'warning'
      : row?.verdict === 'ISSUE'
        ? 'error'
        : row?.verdict === 'WARN'
          ? 'warning'
          : row?.verdict === 'OK'
            ? 'success'
            : 'info';

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(640px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            가공조건 검증
          </Typography>
          {hasValue(parsedSummary) && (
            <Typography variant="caption" color="text.secondary">
              인식한 조건: {parsedSummary}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {summary}
          </Typography>
        </Stack>
      </Alert>

      {status === 'NEED_MORE_INPUT' ? (
        hasValue(guidance) && (
          <Alert severity="info">
            <Typography variant="body2">{guidance}</Typography>
          </Alert>
        )
      ) : (
        <>
          {row && <ToolSheetReviewRowCard row={row} />}

          {hasValue(guidance) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {guidance}
            </Typography>
          )}

          {showAiExplanation && (
            <Alert severity="info" icon={false} sx={{ mt: 1.5, bgcolor: 'action.hover' }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  🤖 AI 해석 (참고용)
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {data.aiExplanation}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  판정과 수치는 기준자료 대조 결과이며, 이 문단은 해석입니다.
                </Typography>
              </Stack>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

// 이미지 유사도(형상 기반 유사 공정) 검색 뷰 - responseType: MACHINING_IMAGE_SIMILAR
const MachiningImageSimilarView = ({ data, message, onFollowUp }) => {
  // 펼친 상세에서 그림을 누르면 전체 화면으로 크게 본다.
  const [zoomPath, setZoomPath] = useState(null);
  const results = Array.isArray(data?.results) ? data.results : [];
  const status = data?.status || (results.length > 0 ? 'OK' : 'EMPTY');
  const summary = data?.summary || message || '';
  const severity = status === 'OK' ? 'info' : 'warning';

  const value = (...items) => items.find((item) => item !== undefined && item !== null && `${item}`.trim() !== '');
  const specText = (item) => value(item.toolSpec, item.toolName, [
    item.toolD ? `D ${item.toolD}` : null,
    item.toolR ? `R ${item.toolR}` : null,
  ].filter(Boolean).join(' / '));
  const speedText = (item) => value(item.feed, item.feeder);

  // shapeCaption은 VLM이 생성한 JSON 문자열(shapeType/features/symmetry)일 수 있다.
  // 파싱에 실패하면 원문을 그대로 caption 텍스트로 보여준다.
  const parseShapeCaption = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(720px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            유사 공정 검색
          </Typography>
          {/* 요약 글은 "업로드한 형상(판형 핀,홀 4개)과 … 5건: 1. … 2. …" 한 덩어리라 읽기 어려웠다(2026-10-08
              사용자 지적). 결과가 있으면 인식한 형상은 칩으로, 건수·최고 유사도는 한 줄로 보이고 목록은 아래 표가 맡는다. */}
          {results.length > 0 ? (() => {
            const m = `${summary}`.match(/업로드한 형상\((.*?)\)/);
            // "판형 핀,홀 4개" = 형태(첫 단어) + 특징(쉼표로 구분). 띄어쓰기로 자르면 "홀 4개"가 둘로 갈라졌다.
            const tags = m ? (() => {
              const [shape, ...rest] = m[1].trim().split(/\s+/);
              return [shape, ...rest.join(' ').split(',')].map((t) => t.trim()).filter(Boolean);
            })() : [];
            const best = Math.max(...results.map((r) => Number(r.score) || 0));
            return (
              <>
                {tags.length > 0 && (
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>올린 도면</Typography>
                    {tags.map((t) => <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 22 }} />)}
                  </Stack>
                )}
                <Typography variant="body2">
                  비슷한 전극 <b>{results.length}건</b> · 최고 유사도 <b>{best}%</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">항목을 누르면 큰 도면과 내용이 펼쳐집니다.</Typography>
              </>
            );
          })() : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {summary}
            </Typography>
          )}
          {/* "캡션 보유 파일 수: 2059" 같은 운영 정보는 결과가 없을 때(왜 없는지 볼 때)만 보여 준다. */}
          {data?.notice && results.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {data.notice}
            </Typography>
          )}
        </Stack>
      </Alert>

      {results.length > 0 && (
        // 컴팩트 테이블 - 유사도 점수(score%)가 핵심 숫자라 오른쪽에 굵게 보여준다. 전극명은
        // 굵게, 형상 캡션/공구 사양은 작은 보조 텍스트로 내린다.
        <CompactListTable
          items={results}
          getRowKey={(item, index) => `${item.sourcePath || item.fileName}-${item.sheetName || ''}-${item.rowNo || index}`}
          // 행을 누르면 큰 도면 + 그 전극의 내용을 펼친다(2026-10-08 사용자 요청: 썸네일만으로는 판단이 안 된다).
          getExpandedContent={(item) => {
            const caption = parseShapeCaption(item.shapeCaption);
            const name = value(item.programName, item.modelName, item.fileName);
            const rows = [
              ['모델', item.modelName],
              ['프로그램', item.programName],
              ['작업', item.workName],
              ['소재', item.material],
              ['크기', item.size],
              ['형상', caption ? [caption.shapeType, ...(Array.isArray(caption.features) ? caption.features : [])].filter(Boolean).join(' · ') : item.shapeCaption],
              ['대칭', caption?.symmetry],
              ['공구', [item.toolNo ? `#${item.toolNo}` : null, specText(item)].filter(Boolean).join(' ')],
              ['조건', [item.rpm ? `RPM ${item.rpm}` : null, speedText(item) ? `Feed ${speedText(item)}` : null, item.pitch ? `Pitch ${item.pitch}` : null, item.minZ ? `Min Z ${item.minZ}` : null].filter(Boolean).join(' · ')],
              ['비고', item.remark],
              ['파일', item.sourcePath
                ? <><RagSourceLink path={item.sourcePath} label={item.fileName} />{item.sheetName ? ` / ${item.sheetName}` : ''}</>
                : [item.fileName, item.sheetName].filter(Boolean).join(' / ')],
            ].filter(([, v]) => v !== undefined && v !== null && `${v}`.trim() !== '');
            return (
              <Stack spacing={1}>
                {item.thumbnailPath && (
                  <RagThumbnail path={item.thumbnailPath} fill onClick={(e) => { e.stopPropagation(); setZoomPath(item.thumbnailPath); }} />
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.5 }}>
                  {rows.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <Typography variant="caption" color="text.secondary">{k}</Typography>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{v}</Typography>
                    </React.Fragment>
                  ))}
                </Box>
                {onFollowUp && name && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => { e.stopPropagation(); onFollowUp(`${name} 공구 사양`); }}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  >
                    이 전극 공구 조건 전체 보기
                  </Button>
                )}
              </Stack>
            );
          }}
          columns={[
            ...(results.some((item) => item.thumbnailPath) ? [{
              key: 'thumb',
              header: '',
              width: 56,
              render: (item) => (item.thumbnailPath ? <RagThumbnail path={item.thumbnailPath} size={44} /> : null),
            }] : []),
            {
              key: 'title',
              header: '전극/프로그램',
              width: '42%',
              render: (item) => {
                const parsedCaption = parseShapeCaption(item.shapeCaption);
                const captionText = parsedCaption
                  ? [parsedCaption.shapeType, ...(Array.isArray(parsedCaption.features) ? parsedCaption.features.slice(0, 3) : [])].filter(Boolean).join(' · ')
                  : (item.shapeCaption || '');
                return renderTitleCell(value(item.programName, item.modelName, item.fileName), captionText, '전극명 없음');
              },
            },
            {
              key: 'spec',
              header: '공구규격',
              width: '28%',
              render: (item) => (
                <Typography variant="caption" color="text.secondary">
                  {/* 공구 정보가 없는 결과가 많아 '-'만 덩그러니 보였다. 없으면 비워 둔다. */}
                  {[item.toolNo ? `#${item.toolNo}` : null, specText(item), item.rpm ? `RPM ${item.rpm}` : null, speedText(item) ? `Feed ${speedText(item)}` : null].filter(Boolean).join(' · ')}
                </Typography>
              ),
            },
            {
              key: 'score',
              header: '유사도',
              width: '15%',
              align: 'right',
              render: (item) => (
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} color="primary.main" noWrap>
                  {item.score}%
                </Typography>
              ),
            },
          ]}
        />
      )}

      <Dialog open={Boolean(zoomPath)} onClose={() => setZoomPath(null)} fullScreen>
        <Box sx={{ position: 'relative', height: '100%', bgcolor: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setZoomPath(null)}>
          {zoomPath && (
            <Box sx={{ width: '100%', '& img': { maxHeight: '90vh !important', border: 'none !important' } }}>
              <RagThumbnail path={zoomPath} fill />
            </Box>
          )}
          <Button variant="contained" size="small" onClick={() => setZoomPath(null)} sx={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 12px)', right: 12 }}>
            닫기
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

// STP/STEP 형상 분석 뷰 - responseType: STP_SHAPE_ANALYSIS
// cad-service(/analyze)가 만든 6면도(previewSvg, 도면→견적 화면과 동일한 build_preview_svg 결과)를
// 그대로 쓴다. XSS 방지를 위해 SVG를 DOM에 직접 삽입(innerHTML/dangerouslySetInnerHTML)하지 않고
// <img src="data:image/svg+xml;base64,...">로만 렌더링한다 - data URI 안의 <script>는 실행되지 않는다.
const StpShapeAnalysisView = ({ data, message, onNavigate }) => {
  const [zoomed, setZoomed] = useState(false);
  const status = data?.status || 'OK';
  const summary = data?.summary || message || '';
  const severity = status === 'OK' ? 'info' : 'warning';
  const previewSvg = data?.previewSvg;

  const previewSrc = React.useMemo(() => {
    if (!previewSvg) return null;
    try {
      return `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(previewSvg)))}`;
    } catch (error) {
      return null;
    }
  }, [previewSvg]);

  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
  const hasDims = [data?.bboxX, data?.bboxY, data?.bboxZ].every((v) => typeof v === 'number');
  const dims = hasDims ? `${data.bboxX.toFixed(1)} × ${data.bboxY.toFixed(1)} × ${data.bboxZ.toFixed(1)} mm` : null;

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(640px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>형상 분석(STP/STEP)</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{summary}</Typography>
        </Stack>
      </Alert>

      {status === 'OK' && (
        <Card sx={{ border: '1px solid #e0e0e0', mb: 1.5 }}>
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {dims && <Chip label={`크기 ${dims}`} size="small" />}
                {typeof data?.volumeCm3 === 'number' && (
                  <Chip label={`부피 ${data.volumeCm3.toFixed(1)} cm³`} size="small" />
                )}
                {data?.isAssembly && (
                  <Chip
                    label={`조립품 · 부품 ${data.partCount}종 / 인스턴스 ${data.instanceCount}개`}
                    size="small"
                    color="secondary"
                  />
                )}
              </Stack>
              {previewSrc && (
                <Box
                  component="img"
                  src={previewSrc}
                  alt="STP 6면도(평면/정면/측면/배면/저면)"
                  onClick={() => setZoomed(true)}
                  sx={{
                    width: '100%', maxHeight: 360, objectFit: 'contain',
                    border: '1px solid #eee', borderRadius: 1, cursor: 'zoom-in', bgcolor: '#fff',
                  }}
                />
              )}
              {previewSrc && (
                <Typography variant="caption" color="text.secondary">그림을 누르면 크게 봅니다.</Typography>
              )}
              {warnings.length > 0 && (
                <Stack spacing={0.25}>
                  {warnings.map((warning, index) => (
                    <Typography key={index} variant="caption" color="text.secondary">
                      ⚠ {warning}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {data?.quoteRoute && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          onClick={() => onNavigate?.(data.quoteRoute)}
          sx={{ textTransform: 'none' }}
        >
          도면→견적 화면에서 이어서 보기
        </Button>
      )}

      <Dialog open={zoomed} onClose={() => setZoomed(false)} fullScreen>
        <Box
          sx={{ position: 'relative', height: '100%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setZoomed(false)}
        >
          {previewSrc && (
            <Box component="img" src={previewSrc} alt="STP 6면도 확대" sx={{ maxWidth: '95vw', maxHeight: '90vh' }} />
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => setZoomed(false)}
            sx={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 12px)', right: 12 }}
          >
            닫기
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

// 업로드 파일 종류 AI 분류 제안 뷰 - responseType: FILE_KIND_SUGGESTION
// 결정론(툴시트/도면)이 못 가리는 나머지 일반 문서(견적서/발주서/거래명세표/사양서/보고서)에 대해서만 온다.
// 어디까지나 제안이다 — 인식이 틀렸을 때 사용자가 바로 알아챌 수 있도록 AI 추정임을 항상 명시한다.
const FileKindSuggestionView = ({ data, onProjectSelect }) => {
  const [showEvidence, setShowEvidence] = useState(false);
  const label = data?.label || '문서';
  const guidance = data?.guidance;
  const actionCommand = data?.actionCommand;
  const evidence = data?.evidence;

  return (
    <Alert
      severity="info"
      sx={{ maxWidth: '100%' }}
      action={actionCommand ? (
        <Button
          size="small"
          variant="contained"
          onClick={() => onProjectSelect && onProjectSelect(actionCommand)}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          등록하기
        </Button>
      ) : undefined}
    >
      <Stack spacing={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          🤖 {label}로 보입니다
        </Typography>
        {guidance && (
          <Typography variant="body2" color="text.secondary">
            {guidance}
          </Typography>
        )}
        {evidence && (
          <Box>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowEvidence(prev => !prev)}
              sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: '0.75rem' }}
            >
              {showEvidence ? '인식 근거 감추기' : '인식 근거 보기'}
            </Button>
            {showEvidence && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', whiteSpace: 'pre-line', wordBreak: 'break-all', mt: 0.5 }}
              >
                인식 근거: {evidence}
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          AI가 문서 내용을 보고 추정한 결과입니다.
        </Typography>
      </Stack>
    </Alert>
  );
};

// 기준 이탈 리포트(실적 전체 스캔) 뷰 - responseType: DEVIATION_REPORT
// 개별 행이 아니라 반복되는 이탈 패턴을 보여주는 게 목적이다: 규격·툴패스별 이탈 패턴(기준자료 갱신 후보),
// 작업(파일)별 이탈, 기준자료에 아예 없는 규격(등록 후보) 3개 섹션.
// verdictMeta/hasValue/formatNumberValue는 TOOL_USAGE/TOOLSHEET_REVIEW와 동일한 것을 그대로 재사용한다.
// 기준/실적 중앙값 + 편차율을 보여주는 공용 2행 셀(실적 굵게 / 기준·편차 작게, 회색) -
// 이탈 리포트의 RPM/Feed 컬럼에서 재사용한다.
const renderDeviationMetricCell = (actual, standard, ratio) => {
  const formatNumber = formatNumberValue;
  const hasRatio = hasValue(ratio);
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 'medium' }} noWrap>
        {formatNumber(actual) ?? '실적없음'}
      </Typography>
      <Typography variant="caption" color={hasRatio ? 'warning.main' : 'text.secondary'} noWrap sx={{ display: 'block' }}>
        기준 {formatNumber(standard) ?? '-'}{hasRatio ? ` · 편차 ${Math.round(ratio * 100)}%` : ''}
      </Typography>
    </>
  );
};

const DeviationReportView = ({ data, message }) => {
  const has = hasValue;
  const summary = data?.summary || message || '';
  const deviationRate = has(data?.deviationRate) ? data.deviationRate : 0;
  const severity = deviationRate >= 30 ? 'error' : deviationRate >= 15 ? 'warning' : 'info';
  const specPatterns = Array.isArray(data?.specPatterns) ? data.specPatterns : [];
  const filePatterns = Array.isArray(data?.filePatterns) ? data.filePatterns : [];
  const missingStandards = Array.isArray(data?.missingStandards) ? data.missingStandards : [];

  const formatDate = (value) => {
    if (!has(value)) return null;
    try {
      return new Date(value).toLocaleString('ko-KR');
    } catch (error) {
      return value;
    }
  };

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(720px, 100%)' }, maxWidth: '100%' }}>
      <Alert severity={severity} sx={{ mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            기준 이탈 리포트
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {summary}
          </Typography>
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
            <Chip label={`이탈률 ${deviationRate}%`} size="small" color={severity} />
            {data?.issueCount > 0 && <Chip label={`문제 ${data.issueCount}`} size="small" color="error" />}
            {data?.warnCount > 0 && <Chip label={`주의 ${data.warnCount}`} size="small" color="warning" />}
            {data?.okCount > 0 && <Chip label={`적합 ${data.okCount}`} size="small" color="success" />}
            {data?.noStandardCount > 0 && <Chip label={`기준없음 ${data.noStandardCount}`} size="small" variant="outlined" />}
          </Stack>
          {has(data?.generatedAt) && (
            <Typography variant="caption" color="text.secondary">
              계산 시각: {formatDate(data.generatedAt)}{has(data?.elapsedMs) ? ` (소요 ${data.elapsedMs}ms)` : ''}
            </Typography>
          )}
        </Stack>
      </Alert>

      {specPatterns.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>반복 이탈 패턴 (상위 {specPatterns.length}건)</Typography>
          <CompactListTable
            items={specPatterns}
            getRowKey={(item, index) => `${item.spec || 'spec'}-${item.toolpath || ''}-${index}`}
            columns={[
              {
                key: 'spec',
                header: '규격',
                width: '34%',
                render: (item) => renderTitleCell(
                  [item.spec, item.toolpath].filter((part) => has(part)).join(' · '),
                  [item.toolShape, item.standardCandidateCount > 1 ? `기준후보 ${item.standardCandidateCount}종` : null].filter(Boolean).join(' · ')
                ),
              },
              {
                key: 'issue',
                header: '문제현황',
                width: '22%',
                render: (item) => renderStageCell(
                  `${item.totalCount ?? 0}건 중 ${item.issueCount ?? 0}건`,
                  item.issueCount > 0 ? 'error' : item.warnCount > 0 ? 'warning' : 'default',
                  'filled'
                ),
              },
              { key: 'rpm', header: 'RPM', width: '22%', render: (item) => renderDeviationMetricCell(item.actualRpmMedian, item.standardRpm, item.rpmDeviationRatio) },
              { key: 'feed', header: 'Feed', width: '22%', render: (item) => renderDeviationMetricCell(item.actualFeedMedian, item.standardFeed, item.feedDeviationRatio) },
            ]}
          />
        </Stack>
      )}

      {filePatterns.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>작업별 이탈 (상위 {filePatterns.length}건)</Typography>
          <CompactListTable
            items={filePatterns}
            getRowKey={(item, index) => `${item.fileName || 'file'}-${index}`}
            columns={[
              { key: 'file', header: '파일명', width: '46%', render: (item) => renderTitleCell(item.fileName, null, '(파일명 없음)') },
              {
                key: 'issue',
                header: '문제',
                width: '20%',
                render: (item) => renderStageCell(`${item.issueCount ?? 0}/${item.totalCount ?? 0}건`, item.issueCount > 0 ? 'error' : 'default', 'filled'),
              },
              {
                key: 'ratio',
                header: '비율',
                width: '34%',
                render: (item) => {
                  const ratioPercent = Math.round((item.issueRatio || 0) * 100);
                  const barColor = ratioPercent >= 50 ? 'error' : ratioPercent > 0 ? 'warning' : 'inherit';
                  return (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, ratioPercent)}
                      color={barColor}
                      sx={{ height: 6, borderRadius: 1 }}
                    />
                  );
                },
              },
            ]}
          />
        </Stack>
      )}

      {missingStandards.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>기준 미등록 (상위 {missingStandards.length}건)</Typography>
          <CompactListTable
            items={missingStandards}
            getRowKey={(item, index) => `${item.spec || 'missing'}-${item.toolpath || ''}-${index}`}
            columns={[
              {
                key: 'spec',
                header: '규격',
                width: '60%',
                render: (item) => renderTitleCell(
                  [item.spec, item.toolpath].filter((part) => has(part)).join(' · '),
                  Array.isArray(item.sampleFiles) && item.sampleFiles.length > 0 ? `예: ${item.sampleFiles.join(', ')}` : null
                ),
              },
              { key: 'count', header: '발생', width: '40%', align: 'right', render: (item) => renderStageCell(`${item.occurrenceCount ?? 0}건`, 'default') },
            ]}
          />
        </Stack>
      )}
    </Box>
  );
};

// 백엔드 BotCapabilityCatalog의 icon 키를 MUI 아이콘 컴포넌트로 매핑
const HELP_SECTION_ICONS = {
  search: <SearchIcon />,
  description: <DescriptionIcon />,
  stats: <StatsIcon />,
  project: <ProjectIcon />,
  file: <FileIcon />,
  help: <HelpIcon />,
};

const HELP_TIP_ICONS = {
  info: <InfoIcon color="info" />,
  file: <FileIcon color="primary" />,
  warning: <WarningIcon color="warning" />,
};

const renderSectionIcon = (key) => HELP_SECTION_ICONS[key] || <HelpIcon />;
const renderTipIcon = (key) => HELP_TIP_ICONS[key] || <InfoIcon color="info" />;

// 🆕 개선된 도움말 컴포넌트 - 백엔드 카탈로그 데이터 기반
const HelpView = ({ data, message, onProjectSelect }) => {
  const title = data?.title || '무엇을 도와드릴까요?';
  const description = data?.description || '프로젝트, 영업 일정, 제조 작업, 웹 리서치를 요청할 수 있습니다.';
  const helpSections = Array.isArray(data?.sections) ? data.sections : [];
  const quickActions = Array.isArray(data?.quickActions) ? data.quickActions : [];
  const primaryActions = quickActions.slice(0, 5);
  const tips = Array.isArray(data?.tips) ? data.tips : [];
  const isFull = data?.mode === 'full' || helpSections.length > 0;
  const notice = typeof message === 'string' && message.trim() && message.trim() !== title
    ? message.trim()
    : null;

  const handleExampleClick = (command) => {
    if (onProjectSelect && command) {
      onProjectSelect(command);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {notice && (
        <Alert severity={notice.startsWith('죄송') ? 'warning' : 'info'} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {notice}
          </Typography>
        </Alert>
      )}

      {/* 환영 메시지 */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            🤖 {title}
          </Typography>
          <Typography variant="body2">{description}</Typography>
        </Stack>
      </Alert>

      {/* 주요 기능 섹션 */}
      {isFull && helpSections.length > 0 && (
        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              <HelpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              주요 기능
            </Typography>

            <Stack spacing={2}>
              {helpSections.map((section, index) => (
                <Accordion key={section.key || index} sx={{ border: '1px solid #e0e0e0' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {renderSectionIcon(section.icon)}
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {section.title}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {Array.isArray(section.content) && section.content.length > 0 && (
                        <List dense>
                          {section.content.map((item, itemIndex) => (
                            <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}

                      {Array.isArray(section.examples) && section.examples.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            💡 예시 명령어:
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {section.examples.map((example, exampleIndex) => (
                              <Chip
                                key={exampleIndex}
                                label={example}
                                size="small"
                                variant="outlined"
                                clickable
                                onClick={() => handleExampleClick(example)}
                                sx={{
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 빠른 실행 */}
      {primaryActions.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            {isFull && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  ⚡ 빠른 실행
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  자주 사용하는 기능들을 바로 실행해보세요.
                </Typography>
              </>
            )}

            <Grid container spacing={1}>
              {primaryActions.map((action, index) => (
                <Grid item xs={12} sm={6} md={isFull ? 3 : 6} key={index}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<span>{action.icon}</span>}
                    onClick={() => handleExampleClick(action.command)}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: 'primary.50'
                      }
                    }}
                  >
                    {action.label}
                  </Button>
                </Grid>
              ))}
            </Grid>

            {!isFull && (
              <Box sx={{ mt: 1.5, textAlign: 'right' }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleExampleClick('전체 도움말')}
                  sx={{ textTransform: 'none' }}
                >
                  전체 도움말
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* 팁 섹션 */}
      {isFull && tips.length > 0 && (
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              💡 사용 팁
            </Typography>

            <List>
              {tips.map((tip, index) => (
                <ListItem key={index}>
                  <ListItemIcon>{renderTipIcon(tip.icon)}</ListItemIcon>
                  <ListItemText primary={tip.primary} secondary={tip.secondary} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// 파일 찾기(대화형) 결과 뷰 - responseType: FILE_LIST
// "올린 파일 찾기"(FileSearchPanel)를 채팅 안에서도 쓸 수 있게, 봇이 찾아준 상위 10건을 표로 보여준다.
// 다운로드/대화방으로 보내기는 FileSearchPanel이 쓰는 것과 같은 길(fileApi.downloadFile, onPick 흐름)을 그대로 쓴다 —
// 두 벌로 두면 한쪽만 고쳐진다.
const FILE_LIST_TYPE_LABEL = {
  design: '설계', material: '원자재', production: '생산', process: '공정',
  qc: 'QC', completion: '생산완료', shipment: '출하',
  project: '프로젝트', chat: '채팅', esg: 'ESG', tech: '기술',
  PURCHASE_ORDER: '발주', profile: '프로필',
};

const FileListView = ({ data, message, listSummary, onPickExistingFile, pickingFile, onOpenFileSearch, onNavigateRoute, onFollowUp }) => {
  const items = Array.isArray(data?.items) ? data.items : [];
  const totalCount = Number.isFinite(data?.totalCount) ? data.totalCount : items.length;
  const [downloadingUuid, setDownloadingUuid] = useState(null);

  const handleDownload = useCallback(async (file) => {
    if (!file?.fileUuid) return;
    setDownloadingUuid(file.fileUuid);
    try {
      const res = await fileApi.downloadFile(file.fileUuid);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileNm || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // 다운로드만 실패한 것이므로 메시지 자체는 그대로 둔다.
    } finally {
      setDownloadingUuid(null);
    }
  }, []);

  return (
    <Box sx={{ width: { xs: '100%', sm: 'min(640px, 100%)' }, maxWidth: '100%' }}>
      {listSummary ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: items.length > 0 ? 1.25 : 0 }}>
          {listSummary.summary}
        </Typography>
      ) : message && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: items.length > 0 ? 1.25 : 0 }}>
          {message}
        </Typography>
      )}

      {items.length > 0 && (
        <>
          <Card sx={{ border: '1px solid #dfe3e8', borderRadius: 1 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 76 }}>종류</TableCell>
                    <TableCell>파일명</TableCell>
                    <TableCell sx={{ width: 84 }}>등록자</TableCell>
                    <TableCell sx={{ width: 92 }}>등록일</TableCell>
                    <TableCell sx={{ width: 96 }} align="center">동작</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((file, index) => (
                    <TableRow key={file.fileUuid || index} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={file.sourceLabel || FILE_LIST_TYPE_LABEL[file.fileTyp] || file.fileTyp || '-'}
                        />
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{file.fileNm || '-'}</TableCell>
                      <TableCell>{file.crtNm || '-'}</TableCell>
                      <TableCell>{String(file.crtDt || '').substring(0, 10) || '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.25} justifyContent="center">
                          <Tooltip title="열기/다운로드">
                            <span>
                              <IconButton
                                size="small"
                                disabled={downloadingUuid === file.fileUuid}
                                onClick={() => handleDownload(file)}
                              >
                                {downloadingUuid === file.fileUuid
                                  ? <CircularProgress size={16} />
                                  : <DownloadIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="대화방으로 보내기">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={pickingFile}
                                onClick={() => onPickExistingFile?.(file)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Card>

          {listSummary ? (
            <BotListFooter
              listSummary={listSummary}
              shownCount={items.length}
              onNavigateRoute={onNavigateRoute}
              onOpenFileSearch={onOpenFileSearch}
              appliedConditions={data?.appliedConditions}
              onFollowUp={onFollowUp}
            />
          ) : (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary">총 {totalCount}건</Typography>
              {totalCount > items.length && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onOpenFileSearch?.(data?.appliedConditions)}
                  sx={{ textTransform: 'none' }}
                >
                  전체 보기
                </Button>
              )}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

// 메인 봇 응답 렌더러
const BotResponseRenderer = ({
  message,
  onProjectSelect,
  onBotMessage,
  alreadyLinked,
  connectedData,
  onPickExistingFile,
  pickingFile,
  onOpenFileSearch,
  onNavigateRoute,
}) => {
  // 견적서 등록 확인 핸들러
  const handleEstimateConfirm = useCallback((data) => {
    // 견적서 등록 폼으로 이동하는 메시지 전송
    if (onProjectSelect) {
      onProjectSelect(`견적서 등록 폼 요청: ${data.tempQteUuid}|${data.confirmationToken || ''}`);
    }
  }, [onProjectSelect]);

  // 견적서 등록 취소 핸들러
  const handleEstimateCancel = useCallback((tempQteUuid, confirmationToken = '') => {
    // 견적서 등록 취소 메시지 전송
    if (onProjectSelect) {
      onProjectSelect(`견적서 등록 취소: ${tempQteUuid}|${confirmationToken}`);
    }
  }, [onProjectSelect]);

  // 견적서 등록 폼 제출 핸들러
  const handleEstimateSubmit = useCallback((tempQteUuid, formData, confirmationToken = '') => {
    // 견적서 등록 요청 메시지 전송
    const submitMessage = `견적서 등록 제출: ${tempQteUuid}|${confirmationToken}|${JSON.stringify(formData)}`;
    if (onProjectSelect) {
      onProjectSelect(submitMessage);
    }
  }, [onProjectSelect]);

  // 🔧 개선된 봇 응답 타입 감지
  const getBotResponseType = (msg) => {
    console.log('🔍 봇 응답 타입 감지:', msg);
    
    // 1. 명시적인 responseType이 있으면 사용
    if (msg.responseType) {
      console.log('✅ 명시적 responseType:', msg.responseType);
      return msg.responseType;
    }
    
    // 2. 도움말 메시지 우선 검사 (더 엄격한 조건)
    if (msg.message) {
      const helpKeywords = ['사용 가이드', '사용법', '도움말', 'KMES BOT 사용', 'PMS BOT 사용', '명령어 안내'];
      const isHelpMessage = helpKeywords.some(keyword => 
        msg.message.includes(keyword)
      );
      
      if (isHelpMessage) {
        console.log('✅ 도움말 메시지 감지');
        return 'HELP';
      }
    }
    
    // 3. 에러 메시지 감지 (도움말보다 후순위)
    if (msg.message && msg.message.startsWith('❌')) {
      console.log('✅ 에러 메시지 감지');
      return 'ERROR';
    }
    
    // 4. 데이터 구조 기반 타입 감지
    if (msg.data && typeof msg.data === 'object') {
    // 파일 업로드 연결 선택 (FILE_UPLOAD_CONNECT)
    if (msg.data.fileName && msg.data.fileType && msg.data.projectFound === false && !msg.data.projectData) {
        console.log('✅ 파일 업로드 연결 선택');
        return 'FILE_UPLOAD_CONNECT';
      }

      // 견적서 등록 확인 데이터
      if (msg.data.tempQteUuid && msg.data.project && msg.data.existingQuotes !== undefined) {
        console.log('✅ 견적서 등록 확인 데이터');
        return 'ESTIMATE_REGISTRATION_CONFIRM';
      }
      
      // 견적서 등록 폼 데이터
      if (msg.data.tempQteUuid && msg.data.defaultValues) {
        console.log('✅ 견적서 등록 폼 데이터');
        return 'ESTIMATE_REGISTRATION_FORM';
      }
      
      // 견적서 등록 완료 데이터
      if (msg.data.registeredQuote && msg.data.summaryMessage) {
        console.log('✅ 견적서 등록 완료 데이터');
        return 'ESTIMATE_REGISTRATION_COMPLETE';
      }
      
      // 파일 분석 결과 (견적서 파일 포함)
      if (msg.data.fileName && msg.data.fileType && msg.data.projectFound !== undefined) {
        console.log('✅ 파일 분석 결과');
        return 'FILE_ANALYSIS';
      }
      
      // 프로젝트 상세 정보
      if (msg.data.prjUuid || (msg.data.prjNo && msg.data.cstNm)) {
        console.log('✅ 프로젝트 상세 정보');
        return 'PROJECT_DETAIL';
      }
    }
    
    // 5. 프로젝트 목록 (배열)
    if (msg.data && Array.isArray(msg.data)) {
      console.log('✅ 프로젝트 목록');
      return 'PROJECT_LIST';
    }
    
    // 6. 일반 텍스트 메시지
    console.log('✅ 일반 텍스트 메시지');
    return 'TEXT';
  };

  const responseType = getBotResponseType(message);
  console.log('🎯 최종 응답 타입:', responseType);

  // 프로젝트 선택 핸들러
  const handleProjectSelect = (project) => {
    if (onProjectSelect) {
      // "프로젝트 선택: PRJ-2024-001" 형태로 메시지 전송
      onProjectSelect(`프로젝트 선택: ${project.prjNo}`);
    }
  };

  const handleNavigate = (url) => {
    openChatUrl(url);
  };

  switch (responseType) {
    case 'PROJECT_LIST': {
      const projectListItems = Array.isArray(message.data) ? message.data : [];
      const projectListSummary = message.listSummary;
      return (
        <Box sx={{ width: { xs: '100%', sm: 'min(640px, 100%)' }, maxWidth: '100%' }}>
          {projectListSummary && (
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {projectListSummary.summary}
            </Typography>
          )}

          {/* 프로젝트 목록 */}
          {projectListItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              검색된 프로젝트가 없습니다.
            </Typography>
          ) : projectListSummary ? (
            // 컴팩트 테이블(B안) - 날짜 컬럼은 버킷 구분이 없는 일반 검색 결과라 "기준일"(매출/수금
            // 예정일)로 통일한다.
            <CompactListTable
              items={projectListItems}
              getRowKey={(project, index) => project.prjUuid || index}
              getActionUrl={(project) => project.actionUrl}
              columns={[
                { key: 'biz', header: '고객 · 사업명', width: '42%', render: (project) => renderTitleCell(project.customerNm, project.cstNm, '거래처 미정') },
                { key: 'stage', header: '단계/상태', width: '24%', render: (project) => renderStageCell(project.stageLabel || project.gradeDescription, 'default') },
                { key: 'amount', header: '금액', width: '17%', align: 'right', render: (project) => renderAmountCell(project.expSales) },
                { key: 'date', header: '기준일', width: '17%', align: 'right', render: (project) => renderDateCell(project.dueDate) },
              ]}
            />
          ) : (
            <Stack spacing={0.75}>
              {projectListItems.map((project, index) => (
                <ProjectListCard
                  key={project.prjUuid || index}
                  project={project}
                  onSelect={handleProjectSelect}
                />
              ))}
            </Stack>
          )}

          {/* 추가 안내 */}
          {projectListItems.length > 0 && !projectListSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 1,
                px: 1,
                py: 0.5,
                backgroundColor: '#f8f9fa',
                borderRadius: 1
              }}
            >
              💡 프로젝트를 선택하면 상세 정보를 확인할 수 있습니다.
            </Typography>
          )}

          {projectListSummary && (
            <BotListFooter
              listSummary={projectListSummary}
              shownCount={projectListItems.length}
              onNavigateRoute={onNavigateRoute}
              onFollowUp={onProjectSelect}
            />
          )}
        </Box>
      );
    }

    case 'PROJECT_DETAIL':
      return (
        <Box sx={{ maxWidth: '100%' }}>
          {/* 프로젝트 상세 정보 */}
          <ProjectDetailView 
            project={message.data}
            onNavigate={handleNavigate}
            onCommand={onProjectSelect}
          />

          {/* 목록으로 돌아가기 버튼 */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              color="primary"
              onClick={() => onProjectSelect && onProjectSelect('프로젝트 검색 목록으로')}
              sx={{ textTransform: 'none' }}
            >
              ← 목록으로 돌아가기
            </Button>
          </Box>
        </Box>
      );

case 'FILE_ANALYSIS':
  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* 타이디 카드 - 파일명(제목) + 프로젝트 연결 여부(상태 배지), 타입/프로젝트 정보는 작은
          보조 텍스트로. 기존 상단 Alert·그리드에 있던 정보는 모두 그대로 담았다. */}
      <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}>
                {message.data.fileName || '파일'}
              </Typography>
              <Chip
                label={message.data.projectFound ? '프로젝트 연결됨' : '프로젝트 미발견'}
                size="small"
                color={message.data.projectFound ? 'success' : 'warning'}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {[
                message.data.fileType ? `타입 ${message.data.fileType}` : null,
                message.data.projectData?.prjNo ? `프로젝트 ${message.data.projectData.prjNo}` : null,
                message.data.projectData?.projectName || message.data.projectData?.cstNm || null,
              ].filter(Boolean).join(' · ')}
            </Typography>
            {message.message && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {message.message}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* 액션 버튼들 */}
      <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
        {/* 프로젝트 정보 확인 버튼 */}
        {message.data.projectFound && message.data.projectData && message.data.projectData.prjNo && (
          <Button
            variant="outlined"
            color="info"
            startIcon={<ProjectIcon />}
            onClick={() => onProjectSelect && onProjectSelect(`프로젝트 선택: ${message.data.projectData.prjNo}`)}
            sx={{ textTransform: 'none' }}
          >
            프로젝트 정보 확인
          </Button>
        )}
        
        {/* 프로젝트 상세보기 버튼 */}
        {message.data.projectFound && message.data.projectData && message.data.projectData.projectDetailUrl && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LaunchIcon />}
            onClick={() => window.open(message.data.projectData.projectDetailUrl, '_blank')}
            sx={{ textTransform: 'none' }}
          >
            프로젝트 상세보기
          </Button>
        )}
        
        {/* 🆕 견적서 등록 진행 버튼 - 수정된 조건 */}
        {message.data.projectFound && 
         (message.data.fileType === '매출견적서' || 
          message.data.fileType === '견적서' || 
          message.data.fileName?.includes('견적서')) && (
          <Button
            variant="contained"
            color="success"
            startIcon={<DescriptionIcon />}
                onClick={() => {
                  // 임시 견적서 UUID가 있으면 사용, 없으면 프로젝트 정보로 견적서 등록 요청
                  if (message.data.tempQteUuid) {
                onProjectSelect && onProjectSelect(`견적서 등록 폼 요청: ${message.data.tempQteUuid}`);
              } else {
                onProjectSelect && onProjectSelect(`견적서 등록 시작: ${message.data.projectData.prjNo}`);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            견적서 등록하기
          </Button>
        )}
        
        {/* 프로젝트를 찾지 못한 경우 프로젝트 검색 버튼 */}
        {!message.data.projectFound && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<BusinessIcon />}
            onClick={() => onProjectSelect && onProjectSelect('프로젝트 검색')}
            sx={{ textTransform: 'none' }}
          >
            프로젝트 검색하기
          </Button>
        )}

      </Stack>
    </Box>
  );
    case 'FILE_UPLOAD_CONNECT':
      return (
        <FileUploadConnectAction
          message={message}
          onProjectSelect={onProjectSelect}
          onBotMessage={onBotMessage}
          alreadyLinked={alreadyLinked}
          connectedData={connectedData}
        />
      );

    case 'FILE_KIND_SUGGESTION':
      return (
        <FileKindSuggestionView
          data={message.data}
          onProjectSelect={onProjectSelect}
        />
      );

    case 'SALES_TASK_SUMMARY':
      return (
        <SalesTaskSummaryView
          data={message.data}
          listSummary={message.listSummary}
          onNavigate={handleNavigate}
          onCommand={onProjectSelect}
          onNavigateRoute={onNavigateRoute}
          onFollowUp={onProjectSelect}
        />
      );

    case 'SALES_SCHEDULE_LIST':
      return (
        <SalesScheduleListView
          data={message.data}
          message={message.message}
          listSummary={message.listSummary}
          onSelect={handleProjectSelect}
          onNavigate={handleNavigate}
          onNavigateRoute={onNavigateRoute}
          onFollowUp={onProjectSelect}
        />
      );

    case 'WEB_RESEARCH':
      return (
        <WebResearchView
          data={message.data}
          message={message.message}
        />
      );

    case 'MANUAL_QA':
      return (
        <ManualQaView
          data={message.data}
          message={message.message}
        />
      );

    case 'MACHINING_RAG':
      return (
        <MachiningRagView
          data={message.data}
          message={message.message}
        />
      );

    case 'TOOL_RECIPE':
      return (
        <ToolRecipeView
          data={message.data}
          message={message.message}
        />
      );

    case 'TOOL_USAGE':
      return (
        <ToolUsageView
          data={message.data}
          message={message.message}
        />
      );

    case 'TOOLSHEET_REVIEW':
      return (
        <ToolSheetReviewView
          data={message.data}
          message={message.message}
        />
      );

    case 'TOOL_CONDITION_CHECK':
      return (
        <ToolConditionCheckView
          data={message.data}
          message={message.message}
        />
      );

    case 'MACHINING_IMAGE_SIMILAR':
      return (
        <MachiningImageSimilarView
          data={message.data}
          message={message.message}
          onFollowUp={onProjectSelect}
        />
      );

    case 'STP_SHAPE_ANALYSIS':
      return (
        <StpShapeAnalysisView
          data={message.data}
          message={message.message}
          onNavigate={handleNavigate}
        />
      );

    case 'DEVIATION_REPORT':
      return (
        <DeviationReportView
          data={message.data}
          message={message.message}
        />
      );

    case 'FILE_LIST':
      return (
        <FileListView
          data={message.data}
          message={message.message}
          listSummary={message.listSummary}
          onPickExistingFile={onPickExistingFile}
          pickingFile={pickingFile}
          onOpenFileSearch={onOpenFileSearch}
          onNavigateRoute={onNavigateRoute}
          onFollowUp={onProjectSelect}
        />
      );

    case 'ESTIMATE_REGISTRATION_CONFIRM':
      return (
        <Box sx={{ maxWidth: '100%' }}>
          <EstimateRegistrationConfirm 
            data={message.data}
            onConfirm={() => handleEstimateConfirm(message.data)}
            onCancel={() => handleEstimateCancel(message.data.tempQteUuid, message.data.confirmationToken)}
          />
        </Box>
      );

    case 'ESTIMATE_REGISTRATION_FORM':
      return (
        <Box sx={{ maxWidth: '100%' }}>
          <EstimateRegistrationForm 
            data={message.data}
            onSubmit={(formData) => handleEstimateSubmit(message.data.tempQteUuid, formData, message.data.confirmationToken)}
            onCancel={() => handleEstimateCancel(message.data.tempQteUuid, message.data.confirmationToken)}
          />
        </Box>
      );

    case 'ESTIMATE_REGISTRATION_COMPLETE':
      return (
        <Box sx={{ maxWidth: '100%' }}>
          <EstimateRegistrationComplete 
            data={message.data}
          />
          
          {/* 목록으로 돌아가기 버튼 */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              color="primary"
              onClick={() => onProjectSelect && onProjectSelect('프로젝트 검색 목록으로')}
              sx={{ textTransform: 'none' }}
            >
              ← 목록으로 돌아가기
            </Button>
          </Box>
        </Box>
      );

    case 'FILE_UPLOAD_CONNECTED':
      return (
        <Alert severity="success" sx={{ maxWidth: '100%' }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              프로젝트 연결 완료
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {message.message || '파일을 프로젝트에 연결했습니다.'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {message.data?.fileName || '파일'} · {message.data?.projectNo || '프로젝트 번호 없음'} ·{' '}
              {message.data?.projectName || '프로젝트명 없음'}
            </Typography>
          </Stack>
        </Alert>
      );

    case 'ERROR':
      return (
        <Alert severity="error" sx={{ maxWidth: '100%' }}>
          <Typography variant="body2">
            {message.message || message.content || message.text}
          </Typography>
        </Alert>
      );

    // 제조 작업 완료 관련 응답 타입
    case 'MANUFACTURING_TASK_TYPE_SELECT':
    case 'MANUFACTURING_TASK_LIST':
    case 'MANUFACTURING_COMPLETE_CONFIRM':
    case 'MANUFACTURING_COMPLETE_SUCCESS':
    case 'MANUFACTURING_NO_TASK':
      return (
        <ManufacturingTaskComplete 
          data={{ ...message.data, responseType: message.responseType }}
          onSendMessage={onProjectSelect}
        />
      );

    case 'HELP':
      // 🆕 새로운 도움말 컴포넌트 사용 - 백엔드 카탈로그 데이터 우선
      return (
        <HelpView
          data={message.data}
          message={message.message}
          onProjectSelect={onProjectSelect}
        />
      );

    case 'SIMPLE':
      return (
        <Alert severity="info" sx={{ maxWidth: '100%' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {message.message || message.content || message.text}
          </Typography>
        </Alert>
      );

    default:
      // 일반 텍스트 메시지.
      // 봇 응답은 content에 BotResponseDto JSON 원문이 그대로 들어있으므로,
      // 전용 뷰가 없는 responseType이라도 파싱된 message를 우선 보여준다(원문 JSON 노출 방지).
      return (
        <Typography sx={{
          whiteSpace: 'pre-line',
          wordBreak: 'break-word'
        }}>
          {message.message || message.text || message.content}
        </Typography>
      );
  }
};

export default BotResponseRenderer;
